import React, { createContext, useContext, useState, useEffect } from "react";

type Language = "nl" | "en" | "de" | "fr";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "nl";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];

    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }

    return typeof value === "string" ? value : key;
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

// Translation data
const translations = {
  nl: {
    nav: {
      companies: "Bedrijven",
      consultants: "Consultants",
      expertise: "Expertise",
      success: "Succesverhalen",
      about: "Over ons",
      vacatures: "Vacatures",
      contact: "Contact",
      organisation: "Onze organisatie",
    },
    hero: {
      title: "Bouw groei met data, AI & tech",
      subtitle: "Dataprofessionals met de ondernemende mentaliteit en drive om uw projecten te laten slagen. Van starter tot senior, groei wordt niet gevonden, maar gebouwd.",
      cta1: "Plan een korte kennismaking",
      cta2: "Ons verhaal",
    },
    companies: {
      title: "Voor bedrijven: Vind de perfecte match",
      subtitle:
        "Of u nu een acute behoefte heeft of een lange termijn partner zoekt - wij matchen u met de juiste expertise binnen 48 uur.",
      benefit1: "Expertise op elk niveau",
      benefit1desc: "Van starter tot senior consultant - wij hebben het talent dat u zoekt",
      benefit2: "Snelle matching",
      benefit2desc: "Binnen 48 uur de eerste geschikte kandidaten in beeld",
      benefit3: "Flexibele inzet",
      benefit3desc: "Per uur, dag, week of maand - volledig naar uw behoeften",
      testimonial:
        "Via New Data Frontier hebben we binnen 48 uur de juiste data engineer gevonden voor ons kritieke migratie project.",
      testimonialAuthor: "Martijn de Vries",
      testimonialRole: "CTO bij TechScale Solutions",
      forCompanies: {
        tag: "New Data Frontier voor bedrijven",
        title: "Expertise waar en wanneer u die nodig heeft",
        subtitle:
          "Van starter tot senior expert - wij matchen uw organisatie met de juiste consultants die direct impact maken. Profiteer van onze jarenlange ervaring in het verbinden van bedrijven met toptalent.",
        benefit1: "Direct toegang tot 250+ expert consultants van starter tot senior niveau",
        benefit2: "Helder uurtarief, geen verborgen kosten - transparantie staat voorop",
        benefit3: "Flexibele inzet vanaf één dag, geen minimum aantal uren",
        benefit4: "Bewezen track record bij 100+ marktleiders met 95%+ succesvolle matches",
        cta: "Vind de perfecte match voor uw project",
        testimonial:
          "Luc en Eli hebben ons enorm geholpen door een, zowel slimme als sociale, expert 6 maanden lang in te zetten als junior mobiliteitsdata-analist bij team Kennis & Onderzoek. De kwaliteit en begeleiding van New Data Frontier overtroffen onze verwachtingen.",
        testimonialAuthor: "Suzanne Kieft",
        testimonialRole: "Vervoerregio Amsterdam",
        testimonialAlt: "New Data Frontier succesverhaal",
        howItWorks: "Hoe het werkt",
        howItWorksSubtitle: "In drie simpele stappen naar de juiste expertise voor uw project",
        step1Title: "Vertel ons wat u zoekt",
        step1Desc: "Vul het contactformulier in of plan een gesprek. We luisteren naar uw behoeften.",
        step2Title: "Wij vinden de perfecte match",
        step2Desc: "Binnen 48 uur selecteren we de beste kandidaten uit ons netwerk van 250+ experts.",
        step3Title: "Start binnen een maand",
        step3Desc: "Na kennismaking starten we vaak binnen een maand, spoedplaatsing binnen 2 weken mogelijk.",
        whatClientsSay: "Wat klanten zeggen",
        transparentRates: "Transparante tarieven",
        transparentRatesSubtitle: "No-nonsense prijzen zonder verborgen kosten",
        downloadFullBrochure: "Download volledige brochure",
        readyToStart: "Klaar om te starten?",
        readyToStartDesc: "Vul hiernaast het contactformulier in of neem direct contact met ons op.",
        planDirectCall: "Plan direct een gesprek",
        sectorsTitle: "Onze sectoren",
        sectorsSubtitle: "Wij zijn sectoragnostisch - onze consultants werken in elke branche. Maar door jarenlange ervaring hebben we specifieke domeinexpertise opgebouwd in de volgende sectoren:",
        sectorBouw: "Bouw",
        sectorBouwDesc: "Data-gedreven projectmanagement, BIM-analyse en procesoptimalisatie in de bouwsector.",
        sectorZorg: "Zorg",
        sectorZorgDesc: "Van zorgdata-analyse tot AI-toepassingen in ziekenhuizen en zorginstellingen.",
        sectorHR: "HR",
        sectorHRDesc: "People analytics, workforce planning en data-gedreven HR-transformatie.",
        sectorPE: "Private Equity",
        sectorPEDesc: "Due diligence data-analyse, portfolio monitoring en waardecreatie met data.",
        sectorAutomotive: "Automotive",
        sectorAutomotiveDesc: "Productiedata, supply chain analytics en kwaliteitsoptimalisatie.",
        sectorScheepvaart: "Scheepvaart",
        sectorScheepvaartDesc: "Maritieme data-analyse, routeoptimalisatie en haven-logistiek.",
      },
    },
    consultants: {
      title: "Voor consultants: Groei in je carrière",
      subtitle:
        "Word onderdeel van ons netwerk en krijg toegang tot de meest uitdagende projecten bij toonaangevende bedrijven.",
      benefit1: "Werk aan uitdagende projecten bij 100+ toonaangevende bedrijven en marktleiders",
      benefit2: "Bouw je expertise én je professionele netwerk uit onder begeleiding van senior experts",
      benefit3: "Flexibele opdrachten perfect passend bij jouw niveau - van starter tot senior",
      benefit4: "Eerlijke, transparante tarieven en altijd op tijd betaald - geen gedoe",
      tag: "Ons aanbod",
      cta: "Start jouw carrière als expert consultant",
      fullFormCta: "Of vul direct het volledige formulier in →",
      testimonial:
        "Na een jaar via New Data Frontier bij Robeco te hebben gewerkt, heeft Robeco mij een aanbieding gedaan om op een intern contract over te stappen. Gezien mijn fijne tijd als externe bij Robeco, besloot ik om die overstap te maken.",
      testimonialAuthor: "Teun Tanis",
      testimonialRole: "Data Consultant",
      testimonialAlt: "Consultant succesverhaal",
      heroTitle: "Groei in je carrière als expert consultant",
      heroSubtitle: "Word onderdeel van ons netwerk van 250+ professionals en krijg toegang tot de meest uitdagende projecten bij toonaangevende bedrijven.",
      howItWorks: "Hoe het werkt",
      howItWorksSubtitle: "In drie simpele stappen naar uitdagende opdrachten die bij jou passen",
      step1Title: "Meld je aan",
      step1Desc: "Vul het aanmeldformulier in en vertel ons over jouw expertise en ambities.",
      step2Title: "Wij matchen je",
      step2Desc: "We zoeken de beste projecten die passen bij jouw niveau en interesses.",
      step3Title: "Start met je opdracht",
      step3Desc: "Werk aan uitdagende projecten bij toonaangevende bedrijven en bouw je carrière uit.",
      successStoriesTitle: "Succesverhalen van consultants",
      whatYouGet: "Wat je krijgt",
      whatYouGetSubtitle: "Als consultant bij New Data Frontier profiteer je van vele voordelen",
      readyToStart: "Klaar om te starten?",
      readyToStartDesc: "Meld je aan en ontdek de beste projecten die bij jouw expertise passen.",
      personalGuidance: "Persoonlijke begeleiding gedurende je opdracht",
      leadingCompanies: "Projecten bij 100+ toonaangevende bedrijven",
      fairRates: "Eerlijke tarieven en snelle uitbetaling",
      fillFormDesc: "Vul hiernaast het aanmeldformulier in als consultant.",
      orFillFullForm: "Of vul direct het volledige formulier in",
    },
    consultantsPage: {
      heroTitle: "Vind de plek die bij jou past",
      heroSubtitle: "Of je nu student bent, starter of ervaren professional, wij hebben een merk dat precies aansluit bij jouw niveau en ambities.",
      heroCta: "Ontdek waar jij thuishoort",
      funnelTitle: "Een dataprofessional voor elke vraag",
      funnelSubtitle: "Zoekt u een specifieke dataprofessional? Maak hieronder uw keuze. Voor alle andere vragen, neem contact met ons op.",
      datastudentName: "DataStudent",
      datastudentTarget: "Junior Data Professionals",
      datastudentDesc: "Nog studerend of net afgestudeerd? Bij DataStudent doe je werkervaring op via detachering bij toonaangevende bedrijven. Flexibel naast je studie of als eerste baan.",
      datastudentCta: "Inschrijven bij DataStudent",
      newdataselectName: "New Data Select",
      newdataselectTarget: "Werving & Selectie",
      newdataselectDesc: "Klaar voor de volgende stap in je carrière? New Data Select matcht jou met de beste werkgevers via werving & selectie. Duurzaam en op maat.",
      newdataselectCta: "Bekijk kansen bij New Data Select",
      dutchdatacompanyName: "Dutch Data Company",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyDesc: "Ervaren specialist of freelancer? Dutch Data Company biedt projectmatige opdrachten bij organisaties die écht impact willen maken met data en AI.",
      dutchdatacompanyCta: "Bekijk projecten bij Dutch Data Company",
      howTitle: "Zo werkt het",
      howSubtitle: "In drie stappen aan de slag bij het merk dat bij jou past",
      step1Title: "Kies jouw merk",
      step1Desc: "Bekijk welk merk past bij jouw ervaring en ambities - van student tot senior.",
      step2Title: "Schrijf je in",
      step2Desc: "Ga naar de website van het merk en meld je aan. Simpel en snel.",
      step3Title: "Start met werken",
      step3Desc: "We matchen je met de beste opdrachten en je gaat direct aan de slag.",
      ctaTitle: "Klaar om te starten?",
      ctaSubtitle: "Kies het merk dat bij je past en zet vandaag nog de eerste stap in jouw data-carrière.",
    },
    expertise: {
      tag: "Onze expertise",
      title: "Specialisten in data, AI en tech",
      subtitle:
        "Software development, data scientists, data analisten, data engineers, dashboarding, SQL, Java developers, advies, migratie, dataScans, hackathons, data opschoning en data labeling. Voor zowel de IT/Data nerd als de MKB-eigenaar die kwaliteit, procesoptimalisatie en Agile/Scrum werkwijzen waardeert.",
      area1: "Data Engineering & SQL",
      area1desc:
        "Van datacollectie tot pipelines en datawarehouses. Onze data engineers bouwen robuuste infrastructuren, optimaliseren SQL queries en zorgen voor naadloze data migraties.",
      area2: "AI & Machine Learning",
      area2desc:
        "Voorspellende modellen, natuurlijke taalverwerking en computer vision. Onze AI-experts brengen state-of-the-art machine learning en data labeling naar uw organisatie.",
      area3: "Data Science & Analytics",
      area3desc:
        "Verander data in waardevolle inzichten. Van data opschoning tot geavanceerde statistische modellen en dataScans - onze data scientists helpen u datagedreven beslissingen te nemen.",
      area4: "Software Development",
      area4desc:
        "Full-stack development met Java, Python en moderne frameworks. Onze developers bouwen schaalbare applicaties, API's en microservices volgens Agile/Scrum methodieken.",
      area5: "Dashboarding & BI",
      area5desc:
        "Interactieve dashboards en rapportages die écht worden gebruikt. Onze BI-specialisten zorgen dat uw data visueel en begrijpelijk wordt, met lean en geoptimaliseerde processen.",
      area6: "Advies & Evenementen",
      area6desc:
        "Strategisch advies voor procesoptimalisatie en digitale transformatie. Van hackathons tot workshops - we helpen u met kennisoverdracht en hands-on innovatie sessies.",
    },
    clients: {
      title: "Vertrouwd door toonaangevende organisaties",
      subtitle: "Van startup tot corporate, onze experts leveren waarde voor organisaties van elk formaat.",
    },
    projects: {
      title: "Toonaangevende projecten",
      subtitle: "Van AI in de sport tot maritieme innovatie, onze experts werken aan de meest uitdagende projecten.",
      project1: "ZAIL: AI in competitieve zeilwedstrijden",
      project1desc: "Geavanceerde AI-systemen voor strategische besluitvorming tijdens internationale zeilwedstrijden.",
      project2: "Dashboarding voor medisch onderzoek",
      project2desc: "Data-analyse en dashboards voor wetenschappelijk onderzoek bij Medizinische Hochschule Hannover.",
      project3: "Ronald McDonald digitaal ondersteunen",
      project3desc: "Digitale transformatie en data-gedreven optimalisatie van processen en dienstverlening",
      project4: "STERK",
      project4desc: "Een samenwerking van Fitter Nederland, Erik Wegewijs en Bas Kremer (Special Forces VIP) waarbij wij als DataStudent ondersteunen in hun data missie.",
    },
    testimonials: {
      title: "Succesverhalen",
      subtitle: "Ontdek hoe bedrijven en consultants succesvol zijn met New Data Frontier",
      anna: {
        quote: "De samenwerking met DataStudent was een uitzonderlijke ervaring voor ons onderzoeksteam bij Medizinische Hochschule Hannover. Twee zeer bekwame studenten ondersteunden ons op afstand en droegen bij aan de statistische analyses en datavisualisaties voor verschillende van onze onderzoekspapers. Ondanks dat ze volledig op afstand werkten, waren hun professionaliteit en vermogen om hoogwaardige resultaten te leveren opmerkelijk.",
        role: "Hannover Medical School",
      },
      jesus: {
        quote: "Tijdens mijn wiskundemaster wilde ik wat praktijkervaring opdoen ter aanvulling op mijn studie, en DataStudent gaf me precies wat ik nodig had. Dit bood de mogelijkheid om mijn programmeervaardigheden te oefenen, en nog belangrijker, mijn communicatieve vaardigheden. Dit werk resulteerde in een gepubliceerde onderzoeksbrief en hielp me zeker bij het worden van een betere kandidaat voor mijn huidige PhD.",
        role: "PhD student Dartmouth, voorheen DataStudent consultant",
      },
      gido: {
        quote: "Als Managing Partner van het Highberg People Analytics & Research team ben ik regelmatig op zoek naar startend Data & Analytics talent. Datastudent kent deze talentmarkt als geen ander. Daarnaast biedt Datastudent een persoonlijke, no-nonsense benadering waarbij ze ook de inhoud van het vakgebied begrijpen. Kortom, ik kan Datastudent zeker aanbevelen als partner!",
        role: "Managing Partner bij Highberg",
      },
      teun: {
        quote: "Na het afronden van mijn master Data Science heeft Datastudent mij geholpen bij het zetten van mijn eerste stappen als data professional. Door middel van een aantal goede gesprekken met Luc ben ik terecht gekomen bij Robeco als Business Intelligence Specialist. Na een jaar via Datastudent bij Robeco te hebben gewerkt, heeft Robeco mij een aanbieding gedaan om op een intern contract over te stappen.",
        role: "Data Engineer bij Robeco, voorheen DataStudent consultant",
      },
      stan: {
        quote: "Sinds april 2024 werk ik bij Datastudent, waar ik de kans heb gekregen om mijn kennis in de praktijk toe te passen. Bij Flanderijn werk ik samen met een team aan het omzetten van uitgebreide datasets naar heldere en visueel aantrekkelijke dashboards. Deze ervaring heeft mij geholpen om niet alleen mijn technische vaardigheden te verbeteren, maar ook mijn communicatie- en samenwerkingsvaardigheden te ontwikkelen.",
        role: "Data Analyst bij Flanderijn via DataStudent",
      },
    },
    about: {
      title: "Dít is New Data Frontier",
      subtitle1:
        "New Data Frontier is de brug tussen ambitieuze bedrijven en expert consultants. Met ons netwerk van gespecialiseerde professionals op elk niveau - van starter tot senior - zijn wij marktleider in data, AI en tech consultancy.",
      subtitle2:
        "Onze missie is helder: we maken data, AI en tech expertise toegankelijk voor iedere organisatie, van startup tot corporate en geven professionals de kans hun kennis in te zetten op échte vraagstukken. Zo halen bedrijven meer waarde uit hun data en technologie, terwijl talenten waardevolle ervaring opdoen en hun expertise uitbreiden.",
      stat1: "Experts en talenten",
      stat2: "Tevreden bedrijven",
      stat3: "Succesvolle matches",
      stat4: "Jaar expertise",
      network: "Onderdeel van ons netwerk",
      networkIntro: "New Data Frontier is de paraplu waaronder drie gespecialiseerde merken opereren:",
      networkAnd: "en",
      networkStarters: "",
      networkBeta: "DataStudent verzorgt detachering van studenten en starters. New Data Select is gespecialiseerd in werving & selectie voor professionals met 0-7 jaar werkervaring. Dutch Data Company levert projectmatige inzet van mediors, seniors en freelancers.",
      networkFulltime: "Samen vormen we een one-stop-shop voor data, AI en tech - van jong talent tot ervaren specialisten. Elk merk heeft zijn eigen focus, zodat u altijd de juiste match krijgt.",
    },
    why: {
      title: "Waarom kiezen voor New Data Frontier?",
      subtitle: "We onderscheiden ons door onze bewezen aanpak, kwaliteit en persoonlijke service.",
      reason1: "Bewezen track record",
      reason1desc: "8+ jaar ervaring, 95%+ succesvolle matches en 100+ tevreden bedrijven.",
      reason2: "Snelle matching",
      reason2desc: "Vaak binnen een maand gestart, spoedplaatsing binnen 2 weken mogelijk.",
      reason3: "Kwaliteit gegarandeerd",
      reason3desc: "Alle consultants zijn gescreend, gecertificeerd en hebben bewezen ervaring.",
      reason4: "Transparante prijzen",
      reason4desc: "No-nonsense tarieven zonder verborgen kosten, u weet precies waar u aan toe bent.",
      reason5: "Persoonlijke aanpak",
      reason5desc: "Direct contact met beslissers en maatwerk in elke match.",
      reason6: "Breed netwerk",
      reason6desc: "250+ experts en consultants op elk niveau en in diverse specialisaties.",
      ctaTitle: "Klaar om de volgende stap te zetten?",
      ctaDescription: "Of je nu op zoek bent naar de juiste expertise voor jouw organisatie, of naar een uitdagend project dat jouw carrière een boost geeft - wij staan klaar om je te helpen.",
      ctaButton1: "Start nu",
      ctaButton2: "Plan een gesprek",
    },
    pricing: {
      title: "Onze diensten en tarieven",
      subtitle:
        "Drie merken, drie diensten - elk afgestemd op uw specifieke behoefte. Van flexibele detachering tot strategische projecten.",
      datastudent: "DataStudent",
      datastudentService: "Detachering",
      datastudentDesc: "Flexibele inzet van studenten en starters op uurbasis",
      datastudentTarget: "Junior Data Professionals",
      datastudentNote: "Ideaal voor data-opdrachten waarbij jong talent direct impact maakt",
      rush: "Spoedplaatsing (binnen 2 weken):",
      rushPrice: "€80",
      perHour: "per uur",
      noMinimum: "Geen minimum aantal uren, flexibel in te zetten",
      newdataselect: "New Data Select",
      newdataselectService: "Werving & Selectie",
      newdataselectDesc: "Duurzame plaatsing van data- en tech-professionals bij uw organisatie",
      newdataselectTarget: "Werving & Selectie",
      newdataselectBenefit1: "Uitgebreid assessment en matching",
      newdataselectBenefit2: "Garantieperiode op elke plaatsing",
      newdataselectBenefit3: "Vaste fee op basis van jaarsalaris",
      newdataselectPrice: "Op basis van offerte",
      dutchdatacompany: "Dutch Data Company",
      dutchdatacompanyService: "Projecten & Consultancy",
      dutchdatacompanyDesc: "Ervaren specialisten voor complexe data- en AI-projecten",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyBenefit1: "Projectmatige inzet met duidelijke scope",
      dutchdatacompanyBenefit2: "Toegang tot senior specialisten en freelancers",
      dutchdatacompanyPrice: "Op basis van offerte",
      forCompanies: "Voor bedrijven",
      forCompaniesDesc:
        "Krijg in één oogopslag inzicht wat voor experts wij leveren, hoe het werkt, wat het kost en waarom andere bedrijven al kozen voor deze aanpak.",
      downloadBrochure: "Download brochure (PDF)",
      brochureNote: "Geen verkooppraatje, gewoon duidelijke info in 5 minuten.",
      planMeeting: "Plan een gesprek",
      planMeetingDesc:
        "Bespreek vrijblijvend jouw situatie met één van onze experts. We helpen je graag verder met de beste match voor jouw organisatie of carrière.",
      schedule: "Plan een afspraak in",
      scheduleNote: "Kies zelf een moment dat jou het beste uitkomt.",
    },
    team: {
      title: "Persoonlijk contact, korte lijnen",
      subtitle:
        "Bij New Data Frontier geloven we dat de beste resultaten ontstaan door écht contact. Geen lange keten van accountmanagers, maar direct schakelen met de mensen die het verschil maken.",
      founder: "Oprichter & Senior Strategic Partner",
      founderAI: "Oprichter CEO & Senior Data Expert",
      linkedin: "LinkedIn profiel",
      closing:
        "Bel, mail of app ons gerust, wij pakken persoonlijk op en denken graag met je mee.",
    },
    mission: {
      title: "Onze missie en impact",
      missionTitle: "Missie",
      missionText: "Data, AI en tech expertise toegankelijk maken voor iedere organisatie, van startup tot corporate.",
      impactTitle: "Sociale impact",
      impactText:
        "Wij helpen mensen met een afstand tot de arbeidsmarkt en werken een aantal uur per maand pro bono voor goede doelen met maatschappelijke impact.",
      sdgTitle: "SDG's",
      sdgText: "We dragen actief bij aan de Sustainable Development Goals van de Verenigde Naties.",
      viewSdg: "Bekijk onze SDG's →",
      charitiesTitle: "Goede doelen die we steunen",
      charitiesNote: "Daarnaast hebben we meer dan 20 studie- en studentenverenigingen in heel Nederland gesponsord.",
      applyText: "Wil je een aanvraag doen voor pro bono werk of sponsoring? Neem contact op via",
    },
    contact: {
      title: "Start vandaag met New Data Frontier",
      subtitle:
        "Of u nu op zoek bent naar de juiste expertise voor uw project of naar uitdagende opdrachten die uw carrière naar een hoger niveau tillen - wij matchen u met de perfecte partner. Met 8+ jaar ervaring en 250+ experts in ons netwerk maken we het verschil.",
      downloadBrochure: "Download onze brochure",
      tabCompany: "Ik ben bedrijf",
      tabConsultant: "Ik ben consultant",
      formTitleCompany: "Aanmelden als bedrijf",
      formTitleConsultant: "Aanmelden als consultant",
      formDescCompany: "Vul onderstaand formulier in en we helpen u met het vinden van de juiste expertise.",
      formDescConsultant: "Meld je aan en ontdek uitdagende projecten die bij jouw niveau en ambities passen.",
      companyName: "Bedrijfsnaam",
      fullName: "Volledige naam",
      email: "E-mailadres",
      phone: "Telefoonnummer",
      experience: "Ervaringsniveau",
      experiencePlaceholder: "Selecteer uw niveau",
      starter: "Starter (0-2 jaar)",
      medior: "Medior (2-5 jaar)",
      senior: "Senior (5+ jaar)",
      messageCompany: "Waar kunnen wij u mee helpen?",
      messageConsultant: "Vertel over uw expertise",
      messagePlaceholder: "Uw bericht...",
      submit: "Verstuur bericht",
      successTitle: "Bedankt voor uw bericht!",
      successDesc: "We nemen binnen 24 uur contact met u op.",
    },
    contactForm: {
      companyTitle: "Aanmelden als bedrijf",
      consultantTitle: "Aanmelden als consultant",
      companyDescription: "Vul onderstaand formulier in en we helpen u met het vinden van de juiste expertise.",
      consultantDescription: "Meld je aan en ontdek uitdagende projecten die bij jouw niveau en ambities passen.",
      companyName: "Bedrijfsnaam",
      fullName: "Volledige naam",
      companyNamePlaceholder: "Uw bedrijfsnaam",
      fullNamePlaceholder: "Uw naam",
      email: "E-mailadres",
      emailPlaceholder: "naam@bedrijf.nl",
      phone: "Telefoonnummer",
      experienceLevel: "Ervaringsniveau",
      selectLevel: "Selecteer uw niveau",
      starter: "Starter (0-2 jaar)",
      medior: "Medior (2-5 jaar)",
      senior: "Senior (5+ jaar)",
      companyMessage: "Waar kunnen wij u mee helpen?",
      consultantMessage: "Vertel over uw expertise",
      companyMessagePlaceholder: "Beschrijf uw behoefte aan expertise...",
      consultantMessagePlaceholder: "Beschrijf uw achtergrond en waar u naar zoekt...",
      submitting: "Versturen...",
      submit: "Verstuur aanmelding",
      successTitle: "Bedankt voor uw bericht!",
      successDescription: "We nemen binnen 24 uur contact met u op.",
      errorTitle: "Er ging iets mis",
      errorDescription: "Probeer het later opnieuw of neem direct contact met ons op.",
    },
    footer: {
      whoWeAre: "Wij zijn",
      description:
        "De marktleider in data, AI en tech consultancy. Verbinden van bedrijven met expert consultants sinds 2017.",
      forCompanies: "Voor bedrijven",
      hireExpertise: "Expertise inhuren",
      expertiseAreas: "Expertisegebieden",
      successStories: "Succesverhalen",
      forConsultants: "Voor consultants",
      findProjects: "Projecten vinden",
      careerDev: "Carrière ontwikkeling",
      aboutUs: "Over ons",
      contact: "Contact",
      rights: "Alle rechten voorbehouden.",
      privacy: "Privacy",
      terms: "Algemene voorwaarden",
      partners: "Samenwerkende partners",
    },
    blog: {
      title: "Blog & Nieuws",
      subtitle: "Blijf op de hoogte van het laatste nieuws, inzichten en trends in data, AI en consultancy.",
      seoTitle: "Blog & Nieuws - New Data Frontier",
      seoDescription: "Lees het laatste nieuws en inzichten van New Data Frontier over data strategie, AI, cybersecurity, governance en talent management.",
      searchPlaceholder: "Zoek in artikelen...",
      filterCategory: "Filter op categorie",
      allCategories: "Alle categorieën",
      noResults: "Geen artikelen gevonden voor deze zoekopdracht.",
      noPosts: "Er zijn nog geen blogartikelen gepubliceerd.",
      notFoundTitle: "Artikel niet gevonden",
      notFoundDesc: "Het artikel dat je zoekt bestaat niet of is niet meer beschikbaar.",
      backToBlog: "Terug naar blog",
      readArticle: "Lees",
      authorRole: "Eigenaar New Data Frontier",
    },
    vacatures: {
      title: "Vacatures",
      subtitle: "Ontdek actuele vacatures in data, AI en tech via ons netwerk bij deBetaStudent.",
      seoTitle: "Vacatures Data & AI",
      seoDescription: "Bekijk actuele vacatures in data, AI en tech. Automatisch gesynchroniseerd met deBetaStudent.nl.",
      breadcrumb: "Vacatures",
      searchPlaceholder: "Zoek vacatures...",
      filterCategory: "Categorie",
      filterLocation: "Locatie",
      allCategories: "Alle categorieën",
      allLocations: "Alle locaties",
      resultsFound: "vacatures gevonden",
      viewJob: "Bekijk vacature",
      noResults: "Geen vacatures gevonden voor deze zoekopdracht.",
      noVacatures: "Er zijn momenteel geen vacatures beschikbaar.",
      checkBack: "Bekijk alle vacatures op",
      poweredBy: "Vacatures via",
      ctaTitle: "Op zoek naar meer kansen?",
      ctaDescription: "Bekijk alle vacatures op deBetaStudent.nl of meld je aan als consultant bij New Data Frontier.",
      ctaButton1: "Alle vacatures op deBetaStudent.nl",
      ctaButton2: "Word consultant",
    },
    organisation: {
      seoTitle: "Onze Organisatie - New Data Frontier",
      seoDescription: "Drie merken, drie diensten, drie niveaus. Ontdek hoe New Data Frontier data-talent op elk niveau levert.",
      tagline: "Onze organisatie",
      heroSubtitle: "A Dutch Data Company",
      heroDescription: "Drie merken. Drie diensten. Drie niveaus. Eén missie: de juiste data-expertise op het juiste moment.",
      brandsTitle: "Drie merken, één netwerk",
      brandsSubtitle: "Elk merk bedient een specifiek segment van de markt - zodat we altijd de juiste match kunnen leveren.",
      threeBrands: "Merken",
      threeServices: "Diensten",
      threeLevels: "Niveaus",
      brand1Audience: "Studenten & starters",
      brand1Service: "Detachering",
      brand1Desc: "Ambitieuze studenten en net-afgestudeerden die hun eerste stappen zetten in data, AI en tech. Flexibel inzetbaar via detachering.",
      brand2Audience: "0-7 jaar werkervaring",
      brand2Service: "Werving & Selectie",
      brand2Desc: "Professionals met 0-7 jaar relevante werkervaring die klaar zijn voor de volgende stap. Geplaatst via werving & selectie voor een duurzame match.",
      brand3Audience: "Mediors & seniors",
      brand3Service: "Projectmatig",
      brand3Desc: "Ervaren specialisten en strategisch adviseurs die projectmatig worden ingezet voor complexe data- en AI-vraagstukken.",
      whyTitle: "Waarom deze structuur?",
      whyDescription: "In de praktijk doen we alles - van detachering tot consultancy, van starter tot senior. Maar deze structuur maakt het voor opdrachtgevers én consultants direct duidelijk welk niveau en welke dienstverlening het beste past.",
      ctaCompanies: "Ik zoek talent",
      ctaConsultants: "Ik ben consultant",
      manifestTitle: "Ons Manifest",
      manifest: "Data is the new frontier.\n\nIt promises unprecedented opportunity\nfor those who choose to pursue it.\n\nBut opportunity alone does not create growth.\n\nGrowth demands more than skills.\nMore than technology.\nMore than access.\n\nIt requires an entrepreneurial mindset.\nThe discipline to think long term.\nThe drive to act in the here and now.\n\nWe develop and deliver data professionals\nwith the mindset and drive to build sustainable growth.\n\nGrowth is the foundation of our culture.\n\nWe enable organisations to grow in this new frontier.\nAnd we develop the people who grow with it.\n\nFrom student to senior.\nFrom potential to performance.\n\nGrowth is not found.\nIt is built.",
      mountainTitle: "The Mountain",
      mountain: "It exists long before anyone climbs it.\nPowerful. Visible. Full of potential.\n\nData is no different.\n\nIt is there.\nWaiting to be explored.\nWaiting to be used.\n\nBut a mountain without infrastructure is not an opportunity.\nIt is uncharted terrain - like data without structure.\n\nGrowth begins when terrain becomes workable.\n\nPaths are defined.\nInfrastructure is built.\nCapabilities are developed.\n\nThat is where the frontier shifts -\nfrom raw landscape\nto usable ground.\n\nWhether you aim to climb higher\nor build your business on solid ground,\nthe conditions matter.\n\nWe pave the way.\n\nWe shape the landscape so sustainable growth becomes possible.\n\nGrowth is not found.\nIt is built.",
    },
  },
  en: {
    nav: {
      companies: "Companies",
      consultants: "Consultants",
      expertise: "Expertise",
      success: "Success Stories",
      about: "About Us",
      vacatures: "Jobs",
      contact: "Contact",
      organisation: "Our organisation",
    },
    hero: {
      title: "Build growth through data, AI & tech",
      subtitle: "Data professionals with the entrepreneurial mindset and drive to make your projects succeed. From starter to senior, growth is not found, it is built.",
      cta1: "Schedule a free consultation",
      cta2: "Our story",
    },
    companies: {
      title: "For Companies: Find the Perfect Match",
      subtitle:
        "Whether you have an acute need or are looking for a long-term partner - we match you with the right expertise within 48 hours.",
      benefit1: "Expertise at Every Level",
      benefit1desc: "From starter to senior consultant - we have the talent you're looking for",
      benefit2: "Fast Matching",
      benefit2desc: "First suitable candidates identified within 48 hours",
      benefit3: "Flexible Deployment",
      benefit3desc: "Per hour, day, week or month - completely according to your needs",
      testimonial:
        "Through New Data Frontier we found the right data engineer within 48 hours for our critical migration project.",
      testimonialAuthor: "Martijn de Vries",
      testimonialRole: "CTO at TechScale Solutions",
      forCompanies: {
        tag: "New Data Frontier for companies",
        title: "Expertise when and where you need it",
        subtitle: "From starter to senior expert - we match your organization with the right consultants who make immediate impact.",
        benefit1: "Direct access to 250+ expert consultants from starter to senior level",
        benefit2: "Clear hourly rate, no hidden costs - transparency comes first",
        benefit3: "Flexible deployment from one day, no minimum hours",
        benefit4: "Proven track record at 100+ market leaders with 95%+ successful matches",
        cta: "Find the perfect match for your project",
        testimonial: "Luc and Eli helped us tremendously by deploying a smart and social expert for 6 months as a junior mobility data analyst. The quality and guidance from New Data Frontier exceeded our expectations.",
        testimonialAuthor: "Suzanne Kieft",
        testimonialRole: "Transport Region Amsterdam",
        testimonialAlt: "New Data Frontier success story",
        howItWorks: "How it works",
        howItWorksSubtitle: "Three simple steps to the right expertise for your project",
        step1Title: "Tell us what you're looking for",
        step1Desc: "Fill out the contact form or schedule a call. We listen to your needs.",
        step2Title: "We find the perfect match",
        step2Desc: "Within 48 hours we select the best candidates from our network of 250+ experts.",
        step3Title: "Start within a month",
        step3Desc: "After introduction, we often start within a month. Rush placement within 2 weeks possible.",
        whatClientsSay: "What clients say",
        transparentRates: "Transparent rates",
        transparentRatesSubtitle: "No-nonsense pricing without hidden costs",
        downloadFullBrochure: "Download full brochure",
        readyToStart: "Ready to start?",
        readyToStartDesc: "Fill out the contact form or contact us directly.",
        planDirectCall: "Schedule a call directly",
        sectorsTitle: "Our sectors",
        sectorsSubtitle: "We are sector-agnostic - our consultants work across every industry. However, through years of experience we have built specific domain expertise in the following sectors:",
        sectorBouw: "Construction",
        sectorBouwDesc: "Data-driven project management, BIM analysis and process optimization in construction.",
        sectorZorg: "Healthcare",
        sectorZorgDesc: "From healthcare data analysis to AI applications in hospitals and care institutions.",
        sectorHR: "HR",
        sectorHRDesc: "People analytics, workforce planning and data-driven HR transformation.",
        sectorPE: "Private Equity",
        sectorPEDesc: "Due diligence data analysis, portfolio monitoring and value creation with data.",
        sectorAutomotive: "Automotive",
        sectorAutomotiveDesc: "Production data, supply chain analytics and quality optimization.",
        sectorScheepvaart: "Maritime",
        sectorScheepvaartDesc: "Maritime data analysis, route optimization and port logistics.",
      },
    },
    consultants: {
      title: "For Consultants: Grow Your Career",
      subtitle: "Become part of our network and get access to the most challenging projects at leading companies.",
      benefit1: "Work on challenging projects at 100+ leading companies and market leaders",
      benefit2: "Build your expertise and professional network under guidance of senior experts",
      benefit3: "Flexible assignments perfectly matching your level - from starter to senior",
      benefit4: "Fair, transparent rates and always paid on time - no hassle",
      tag: "New Data Frontier for consultants",
      cta: "Start your career as expert consultant",
      fullFormCta: "Or fill out the complete form directly →",
      testimonial:
        "After working at Robeco for a year through New Data Frontier, Robeco offered me to switch to an internal contract. Given my great time as an external consultant at Robeco, I decided to make that transition.",
      testimonialAuthor: "Teun Tanis",
      testimonialRole: "Data Consultant",
      testimonialAlt: "Consultant success story",
      heroTitle: "Grow your career as an expert consultant",
      heroSubtitle: "Become part of our network of 250+ professionals and get access to the most challenging projects at leading companies.",
      howItWorks: "How it works",
      howItWorksSubtitle: "Three simple steps to challenging assignments that fit you",
      step1Title: "Sign up",
      step1Desc: "Fill out the registration form and tell us about your expertise and ambitions.",
      step2Title: "We match you",
      step2Desc: "We find the best projects that fit your level and interests.",
      step3Title: "Start your assignment",
      step3Desc: "Work on challenging projects at leading companies and build your career.",
      successStoriesTitle: "Consultant success stories",
      whatYouGet: "What you get",
      whatYouGetSubtitle: "As a consultant at New Data Frontier you benefit from many advantages",
      readyToStart: "Ready to start?",
      readyToStartDesc: "Sign up and discover the best projects that match your expertise.",
      personalGuidance: "Personal guidance throughout your assignment",
      leadingCompanies: "Projects at 100+ leading companies",
      fairRates: "Fair rates and fast payment",
      fillFormDesc: "Fill out the registration form as a consultant.",
      orFillFullForm: "Or fill out the complete form directly",
    },
    consultantsPage: {
      heroTitle: "Find the right place for you",
      heroSubtitle: "Whether you're a student, starter or experienced professional, we have a brand that fits your level and ambitions perfectly.",
      heroCta: "Discover where you belong",
      funnelTitle: "A data professional for every need",
      funnelSubtitle: "Looking for a specific data professional? Make your choice below. For all other questions, contact us.",
      datastudentName: "DataStudent",
      datastudentTarget: "Junior Data Professionals",
      datastudentDesc: "Still studying or recently graduated? At DataStudent you gain work experience through secondment at leading companies. Flexible alongside your studies or as your first job.",
      datastudentCta: "Sign up at DataStudent",
      newdataselectName: "New Data Select",
      newdataselectTarget: "Recruitment & Selection",
      newdataselectDesc: "Ready for the next step in your career? New Data Select matches you with the best employers through recruitment & selection. Sustainable and tailored.",
      newdataselectCta: "View opportunities at New Data Select",
      dutchdatacompanyName: "Dutch Data Company",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyDesc: "Experienced specialist or freelancer? Dutch Data Company offers project-based assignments at organizations that want to make real impact with data and AI.",
      dutchdatacompanyCta: "View projects at Dutch Data Company",
      howTitle: "How it works",
      howSubtitle: "Three steps to get started at the brand that suits you",
      step1Title: "Choose your brand",
      step1Desc: "See which brand matches your experience and ambitions - from student to senior.",
      step2Title: "Sign up",
      step2Desc: "Go to the brand's website and register. Simple and fast.",
      step3Title: "Start working",
      step3Desc: "We match you with the best assignments and you get started right away.",
      ctaTitle: "Ready to start?",
      ctaSubtitle: "Choose the brand that fits you and take the first step in your data career today.",
    },
    expertise: {
      tag: "Our Expertise",
      title: "Specialists in Data, AI and Tech",
      subtitle:
        "Software development, data scientists, data analysts, data engineers, dashboarding, SQL, Java developers, consulting, migration, dataScans, hackathons, data cleansing and data labeling. For both the IT/Data expert and the SME owner who values quality, process optimization and Agile/Scrum methodologies.",
      area1: "Data Engineering & SQL",
      area1desc:
        "From data collection to pipelines and data warehouses. Our data engineers build robust infrastructures, optimize SQL queries and ensure seamless data migrations.",
      area2: "AI & Machine Learning",
      area2desc:
        "Predictive models, natural language processing and computer vision. Our AI experts bring state-of-the-art machine learning and data labeling to your organization.",
      area3: "Data Science & Analytics",
      area3desc:
        "Transform data into valuable insights. From data cleansing to advanced statistical models and dataScans - our data scientists help you make data-driven decisions.",
      area4: "Software Development",
      area4desc:
        "Full-stack development with Java, Python and modern frameworks. Our developers build scalable applications, APIs and microservices using Agile/Scrum methodologies.",
      area5: "Dashboarding & BI",
      area5desc:
        "Interactive dashboards and reports that are actually used. Our BI specialists ensure your data becomes visual and understandable, with lean and optimized processes.",
      area6: "Consulting & Events",
      area6desc:
        "Strategic advice for process optimization and digital transformation. From hackathons to workshops - we help you with knowledge transfer and hands-on innovation sessions.",
    },
    clients: {
      title: "Trusted by Leading Organizations",
      subtitle: "From startup to corporate - our experts deliver value for organizations of every size",
    },
    projects: {
      title: "Leading Projects",
      subtitle: "From AI in sports to maritime innovation - our experts work on the most challenging projects",
      project1: "ZAIL: AI in Competitive Sailing",
      project1desc: "Advanced AI systems for strategic decision-making during international sailing competitions",
      project2: "Dashboarding for Medical Research",
      project2desc: "Data analysis and visualization dashboards for scientific research at Medizinische Hochschule Hannover",
      project3: "Ronald McDonald Digital Support",
      project3desc: "Digital transformation and data-driven optimization of processes and services",
      project4: "STERK",
      project4desc: "A collaboration of Fitter Nederland, Erik Wegewijs and Bas Kremer (Special Forces VIP) where we as DataStudent support their data mission",
    },
    testimonials: {
      title: "Success Stories",
      subtitle: "Discover how companies and consultants succeed with New Data Frontier",
      anna: {
        quote: "Collaborating with DataStudent has been an exceptional experience for our research team at Medizinische Hochschule Hannover. Two highly skilled students supported us remotely, contributing to the statistical analyses and data visualizations for several of our research papers. Despite working entirely remotely, their professionalism and ability to deliver high-quality results were remarkable.",
        role: "Hannover Medical School",
      },
      jesus: {
        quote: "While doing my Math master I wanted to gain some real world experience to complement my graduate courses, and DataStudent gave me exactly what I needed. This offered the opportunity to practice my programming skills, and even more importantly, communication skills. This work ended up in a research letter published, and definitely helped me in being a better candidate for my current PhD.",
        role: "PhD student Dartmouth, former DataStudent consultant",
      },
      gido: {
        quote: "As Managing Partner of the Highberg People Analytics & Research team, I am regularly looking for starting Data & Analytics talent. Datastudent knows this talent market like no other. In addition, Datastudent offers a personal, no-nonsense approach where they also understand the content of the field. In short, I can definitely recommend Datastudent as a partner!",
        role: "Managing Partner at Highberg",
      },
      teun: {
        quote: "After completing my master's in Data Science, Datastudent helped me take my first steps as a data professional. Through several good conversations with Luc, I ended up at Robeco as a Business Intelligence Specialist. After working at Robeco for a year via Datastudent, Robeco offered me to switch to an internal contract.",
        role: "Data Engineer at Robeco, former DataStudent consultant",
      },
      stan: {
        quote: "Since April 2024, I have been working at Datastudent, where I have been given the opportunity to apply my knowledge in practice. At Flanderijn, I work with a team on converting extensive datasets into clear and visually attractive dashboards. This experience has helped me improve not only my technical skills but also my communication and collaboration skills.",
        role: "Data Analyst at Flanderijn via DataStudent",
      },
    },
    about: {
      title: "This is New Data Frontier",
      subtitle1:
        "New Data Frontier is the bridge between ambitious companies and expert consultants. With our network of specialized professionals at every level - from starter to senior - we are market leaders in data, AI and tech consultancy.",
      subtitle2:
        "Our mission is clear: we make data, AI and tech expertise accessible to every organization - from startup to corporate - and give professionals the opportunity to apply their knowledge to real challenges. This way, companies get more value from their data and technology, while talents gain valuable experience and expand their expertise.",
      stat1: "Experts and Talents",
      stat2: "Satisfied Companies",
      stat3: "Successful Matches",
      stat4: "Years of Expertise",
      network: "Part of Our Network",
      networkIntro: "New Data Frontier is the umbrella under which three specialized brands operate:",
      networkAnd: "and",
      networkStarters: "",
      networkBeta: "DataStudent handles secondment of students and starters. New Data Select specializes in recruitment for professionals with 0-7 years of experience. Dutch Data Company provides project-based deployment of mid-level, senior professionals, and freelancers.",
      networkFulltime: "Together we form a one-stop-shop for data, AI, and tech - from young talent to experienced specialists. Each brand has its own focus, ensuring you always get the right match.",
    },
    why: {
      title: "Why Choose New Data Frontier?",
      subtitle: "We distinguish ourselves through our proven approach, quality and personal service",
      reason1: "Proven Track Record",
      reason1desc: "8+ years of experience, 95%+ successful matches and 100+ satisfied companies",
      reason2: "Fast Matching",
      reason2desc: "Often started within a month, rush placement within 2 weeks possible",
      reason3: "Quality Guaranteed",
      reason3desc: "All consultants are screened, certified and have proven experience",
      reason4: "Transparent Pricing",
      reason4desc: "No-nonsense rates without hidden costs - you know exactly what to expect",
      reason5: "Personal Approach",
      reason5desc: "Direct contact with decision-makers and customization in every match",
      reason6: "Broad Network",
      reason6desc: "250+ experts and consultants at every level and in various specializations",
      ctaTitle: "Ready to take the next step?",
      ctaDescription: "Whether you're looking for the right expertise for your organization, or a challenging project that boosts your career - we're here to help.",
      ctaButton1: "Start now",
      ctaButton2: "Schedule a call",
    },
    pricing: {
      title: "Our Services and Rates",
      subtitle: "Three brands, three services - each tailored to your specific needs. From flexible secondment to strategic projects.",
      datastudent: "DataStudent",
      datastudentService: "Secondment",
      datastudentDesc: "Flexible deployment of students and starters on hourly basis",
      datastudentTarget: "Junior Data Professionals",
      datastudentNote: "Ideal for data assignments where young talent makes immediate impact",
      rush: "Rush placement (within 2 weeks):",
      rushPrice: "€80",
      perHour: "per hour",
      noMinimum: "No minimum hours, flexible deployment",
      newdataselect: "New Data Select",
      newdataselectService: "Recruitment",
      newdataselectDesc: "Permanent placement of data and tech professionals at your organization",
      newdataselectTarget: "Recruitment & Selection",
      newdataselectBenefit1: "Comprehensive assessment and matching",
      newdataselectBenefit2: "Guarantee period on every placement",
      newdataselectBenefit3: "Fixed fee based on annual salary",
      newdataselectPrice: "Based on quotation",
      dutchdatacompany: "Dutch Data Company",
      dutchdatacompanyService: "Projects & Consultancy",
      dutchdatacompanyDesc: "Experienced specialists for complex data and AI projects",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyBenefit1: "Project-based deployment with clear scope",
      dutchdatacompanyBenefit2: "Access to senior specialists and freelancers",
      dutchdatacompanyPrice: "Based on quotation",
      forCompanies: "For Companies",
      forCompaniesDesc: "Get an overview at a glance of what experts we deliver, how it works, what it costs and why other companies have chosen this approach.",
      downloadBrochure: "Download Brochure (PDF)",
      brochureNote: "No sales pitch, just clear information in 5 minutes.",
      planMeeting: "Schedule a Meeting",
      planMeetingDesc: "Discuss your situation with one of our experts without obligation.",
      schedule: "Schedule an Appointment",
      scheduleNote: "Choose a time that suits you best.",
    },
    team: {
      title: "Personal contact, short lines",
      subtitle:
        "At New Data Frontier, we believe the best results come from real connection. No long chains of account managers - you deal directly with the people who make the difference.",
      founder: "Founder & Senior Strategic Partner",
      founderAI: "Founder CEO & Senior Data Expert",
      linkedin: "LinkedIn Profile",
      closing:
        "Call, email or message us - we'll personally pick up and are happy to think along with you.",
    },
    mission: {
      title: "Our Mission and Impact",
      missionTitle: "Mission",
      missionText: "Making data, AI and tech expertise accessible to every organization, from startup to corporate.",
      impactTitle: "Social Impact",
      impactText:
        "We help companies and people with distance from the labor market. Working pro bono for charities with social impact.",
      sdgTitle: "SDGs",
      sdgText: "We actively contribute to the United Nations Sustainable Development Goals.",
      viewSdg: "View Our SDGs →",
      charitiesTitle: "Charities We Support",
      charitiesNote:
        "In addition, we have sponsored more than 20 study and student associations throughout the Netherlands.",
      applyText: "Would you like to apply for pro bono work or sponsorship? Contact us via",
    },
    contact: {
      title: "Start with New Data Frontier Today",
      subtitle:
        "Whether you're looking for the right expertise for your project or challenging assignments that take your career to the next level - we match you with the perfect partner. With 8+ years of experience and 250+ experts in our network, we make the difference.",
      downloadBrochure: "Download Our Brochure",
      tabCompany: "I'm a Company",
      tabConsultant: "I'm a Consultant",
      formTitleCompany: "Register as Company",
      formTitleConsultant: "Register as Consultant",
      formDescCompany: "Fill in the form below and we'll help you find the right expertise.",
      formDescConsultant: "Register and discover challenging projects that match your level and ambitions.",
      companyName: "Company Name",
      fullName: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      experience: "Experience Level",
      experiencePlaceholder: "Select your level",
      starter: "Starter (0-2 years)",
      medior: "Medior (2-5 years)",
      senior: "Senior (5+ years)",
      messageCompany: "How can we help you?",
      messageConsultant: "Tell us about your expertise",
      messagePlaceholder: "Your message...",
      submit: "Send Message",
      successTitle: "Thank you for your message!",
      successDesc: "We will contact you within 24 hours.",
    },
    contactForm: {
      companyTitle: "Register as a company",
      consultantTitle: "Register as a consultant",
      companyDescription: "Fill out the form below and we will help you find the right expertise.",
      consultantDescription: "Sign up and discover challenging projects that match your level and ambitions.",
      companyName: "Company name",
      fullName: "Full name",
      companyNamePlaceholder: "Your company name",
      fullNamePlaceholder: "Your name",
      email: "Email address",
      emailPlaceholder: "name@company.com",
      phone: "Phone number",
      experienceLevel: "Experience level",
      selectLevel: "Select your level",
      starter: "Starter (0-2 years)",
      medior: "Medior (2-5 years)",
      senior: "Senior (5+ years)",
      companyMessage: "How can we help you?",
      consultantMessage: "Tell us about your expertise",
      companyMessagePlaceholder: "Describe your expertise needs...",
      consultantMessagePlaceholder: "Describe your background and what you're looking for...",
      submitting: "Sending...",
      submit: "Submit registration",
      successTitle: "Thank you for your message!",
      successDescription: "We will contact you within 24 hours.",
      errorTitle: "Something went wrong",
      errorDescription: "Please try again later or contact us directly.",
    },
    footer: {
      whoWeAre: "We are",
      description:
        "The market leader in data, AI and tech consultancy. Connecting companies with expert consultants since 2017.",
      forCompanies: "For Companies",
      hireExpertise: "Hire Expertise",
      expertiseAreas: "Expertise Areas",
      successStories: "Success Stories",
      forConsultants: "For Consultants",
      findProjects: "Find Projects",
      careerDev: "Career Development",
      aboutUs: "About Us",
      contact: "Contact",
      rights: "All rights reserved.",
      privacy: "Privacy",
      terms: "Terms & Conditions",
      partners: "Partner Network",
    },
    blog: {
      title: "Blog & News",
      subtitle: "Stay up to date with the latest news, insights and trends in data, AI and consultancy.",
      seoTitle: "Blog & News - New Data Frontier",
      seoDescription: "Read the latest news and insights from New Data Frontier about data strategy, AI, cybersecurity, governance and talent management.",
      searchPlaceholder: "Search articles...",
      filterCategory: "Filter by category",
      allCategories: "All categories",
      noResults: "No articles found for this search.",
      noPosts: "No blog articles have been published yet.",
      notFoundTitle: "Article not found",
      notFoundDesc: "The article you are looking for does not exist or is no longer available.",
      backToBlog: "Back to blog",
      readArticle: "Read",
      authorRole: "Owner New Data Frontier",
    },
    vacatures: {
      title: "Job Openings",
      subtitle: "Discover current job openings in data, AI and tech through our network at deBetaStudent.",
      seoTitle: "Jobs Data & AI",
      seoDescription: "View current job openings in data, AI and tech. Automatically synced with deBetaStudent.nl.",
      breadcrumb: "Jobs",
      searchPlaceholder: "Search jobs...",
      filterCategory: "Category",
      filterLocation: "Location",
      allCategories: "All categories",
      allLocations: "All locations",
      resultsFound: "jobs found",
      viewJob: "View job",
      noResults: "No jobs found for this search.",
      noVacatures: "No job openings available at the moment.",
      checkBack: "View all jobs at",
      poweredBy: "Jobs via",
      ctaTitle: "Looking for more opportunities?",
      ctaDescription: "View all jobs at deBetaStudent.nl or sign up as a consultant at New Data Frontier.",
      ctaButton1: "All jobs at deBetaStudent.nl",
      ctaButton2: "Become a consultant",
    },
    organisation: {
      seoTitle: "Our Organisation - New Data Frontier",
      seoDescription: "Three brands, three services, three levels. Discover how New Data Frontier delivers data talent at every level.",
      tagline: "Our organisation",
      heroSubtitle: "A Dutch Data Company",
      heroDescription: "Three brands. Three services. Three levels. One mission: the right data expertise at the right time.",
      brandsTitle: "Three brands, one network",
      brandsSubtitle: "Each brand serves a specific market segment - so we can always deliver the right match.",
      threeBrands: "Brands",
      threeServices: "Services",
      threeLevels: "Levels",
      brand1Audience: "Students & starters",
      brand1Service: "Secondment",
      brand1Desc: "Ambitious students and recent graduates taking their first steps in data, AI and tech. Flexibly deployed via secondment.",
      brand2Audience: "0-7 years of experience",
      brand2Service: "Recruitment",
      brand2Desc: "Professionals with 0-7 years of relevant experience ready for the next step. Placed via recruitment for a lasting match.",
      brand3Audience: "Mid-level & seniors",
      brand3Service: "Project-based",
      brand3Desc: "Experienced specialists and strategic advisors deployed on a project basis for complex data and AI challenges.",
      whyTitle: "Why this structure?",
      whyDescription: "In practice, we do everything - from secondment to consultancy, from starter to senior. But this structure makes it immediately clear for both clients and consultants which level and service fits best.",
      ctaCompanies: "I'm looking for talent",
      ctaConsultants: "I'm a consultant",
      manifestTitle: "Our Manifest",
      manifest: "Data is the new frontier.\n\nIt promises unprecedented opportunity\nfor those who choose to pursue it.\n\nBut opportunity alone does not create growth.\n\nGrowth demands more than skills.\nMore than technology.\nMore than access.\n\nIt requires an entrepreneurial mindset.\nThe discipline to think long term.\nThe drive to act in the here and now.\n\nWe develop and deliver data professionals\nwith the mindset and drive to build sustainable growth.\n\nGrowth is the foundation of our culture.\n\nWe enable organisations to grow in this new frontier.\nAnd we develop the people who grow with it.\n\nFrom student to senior.\nFrom potential to performance.\n\nGrowth is not found.\nIt is built.",
      mountainTitle: "The Mountain",
      mountain: "It exists long before anyone climbs it.\nPowerful. Visible. Full of potential.\n\nData is no different.\n\nIt is there.\nWaiting to be explored.\nWaiting to be used.\n\nBut a mountain without infrastructure is not an opportunity.\nIt is uncharted terrain - like data without structure.\n\nGrowth begins when terrain becomes workable.\n\nPaths are defined.\nInfrastructure is built.\nCapabilities are developed.\n\nThat is where the frontier shifts -\nfrom raw landscape\nto usable ground.\n\nWhether you aim to climb higher\nor build your business on solid ground,\nthe conditions matter.\n\nWe pave the way.\n\nWe shape the landscape so sustainable growth becomes possible.\n\nGrowth is not found.\nIt is built.",
    },
  },
  de: {
    nav: {
      companies: "Unternehmen",
      consultants: "Berater",
      expertise: "Expertise",
      success: "Erfolgsgeschichten",
      about: "Über uns",
      vacatures: "Stellenangebote",
      contact: "Kontakt",
      organisation: "Unsere Organisation",
    },
    hero: {
      title: "Wachstum durch Daten, KI & Technologie",
      subtitle: "Datenprofis mit unternehmerischer Denkweise und dem Antrieb, Ihre Projekte zum Erfolg zu führen. Vom Einsteiger bis zum Senior, Wachstum wird nicht gefunden, sondern aufgebaut.",
      cta1: "Kostenloses Gespräch vereinbaren",
      cta2: "Unsere Geschichte",
    },
    companies: {
      title: "Für Unternehmen: Finden Sie das perfekte Match",
      subtitle:
        "Ob Sie einen akuten Bedarf haben oder einen langfristigen Partner suchen - wir vermitteln Ihnen innerhalb von 48 Stunden die richtige Expertise.",
      benefit1: "Expertise auf jedem Niveau",
      benefit1desc: "Von Einsteiger bis Senior Berater - wir haben das Talent, das Sie suchen",
      benefit2: "Schnelle Vermittlung",
      benefit2desc: "Erste geeignete Kandidaten innerhalb von 48 Stunden",
      benefit3: "Flexibler Einsatz",
      benefit3desc: "Pro Stunde, Tag, Woche oder Monat - ganz nach Ihren Bedürfnissen",
      testimonial:
        "Über New Data Frontier haben wir innerhalb von 48 Stunden den richtigen Data Engineer für unser kritisches Migrationsprojekt gefunden.",
      testimonialAuthor: "Martijn de Vries",
      testimonialRole: "CTO bei TechScale Solutions",
      forCompanies: {
        tag: "New Data Frontier für Unternehmen",
        title: "Expertise wann und wo Sie sie brauchen",
        subtitle: "Von Einsteiger bis Senior - wir vermitteln Ihre Organisation mit den richtigen Beratern.",
        benefit1: "Direkter Zugang zu 250+ Experten-Beratern",
        benefit2: "Klarer Stundensatz, keine versteckten Kosten",
        benefit3: "Flexibler Einsatz ab einem Tag",
        benefit4: "Bewährte Erfolgsbilanz bei 100+ Marktführern",
        cta: "Finden Sie das perfekte Match für Ihr Projekt",
        testimonial: "Luc und Eli haben uns enorm geholfen. Die Qualität und Betreuung von New Data Frontier übertrafen unsere Erwartungen.",
        testimonialAuthor: "Suzanne Kieft",
        testimonialRole: "Verkehrsregion Amsterdam",
        testimonialAlt: "New Data Frontier Erfolgsgeschichte",
        howItWorks: "So funktioniert es",
        howItWorksSubtitle: "Drei einfache Schritte zur richtigen Expertise",
        step1Title: "Sagen Sie uns, was Sie suchen",
        step1Desc: "Füllen Sie das Kontaktformular aus oder vereinbaren Sie ein Gespräch.",
        step2Title: "Wir finden das perfekte Match",
        step2Desc: "Innerhalb von 48 Stunden wählen wir die besten Kandidaten aus.",
        step3Title: "Start innerhalb eines Monats",
        step3Desc: "Nach der Vorstellung starten wir oft innerhalb eines Monats.",
        whatClientsSay: "Was Kunden sagen",
        transparentRates: "Transparente Tarife",
        transparentRatesSubtitle: "Klare Preise ohne versteckte Kosten",
        downloadFullBrochure: "Vollständige Broschüre herunterladen",
        readyToStart: "Bereit zu starten?",
        readyToStartDesc: "Füllen Sie das Kontaktformular aus oder kontaktieren Sie uns direkt.",
        planDirectCall: "Gespräch direkt planen",
        sectorsTitle: "Unsere Branchen",
        sectorsSubtitle: "Wir sind branchenunabhängig - unsere Berater arbeiten in jeder Branche. Durch langjährige Erfahrung haben wir jedoch spezifische Fachkompetenz in folgenden Sektoren aufgebaut:",
        sectorBouw: "Bauwesen",
        sectorBouwDesc: "Datengetriebenes Projektmanagement, BIM-Analyse und Prozessoptimierung im Bauwesen.",
        sectorZorg: "Gesundheitswesen",
        sectorZorgDesc: "Von Gesundheitsdatenanalyse bis KI-Anwendungen in Krankenhäusern.",
        sectorHR: "HR",
        sectorHRDesc: "People Analytics, Personalplanung und datengetriebene HR-Transformation.",
        sectorPE: "Private Equity",
        sectorPEDesc: "Due-Diligence-Datenanalyse, Portfolio-Monitoring und Wertschöpfung mit Daten.",
        sectorAutomotive: "Automotive",
        sectorAutomotiveDesc: "Produktionsdaten, Supply-Chain-Analytics und Qualitätsoptimierung.",
        sectorScheepvaart: "Schifffahrt",
        sectorScheepvaartDesc: "Maritime Datenanalyse, Routenoptimierung und Hafenlogistik.",
      },
    },
    consultants: {
      title: "Für Berater: Wachsen Sie in Ihrer Karriere",
      subtitle:
        "Werden Sie Teil unseres Netzwerks und erhalten Sie Zugang zu den anspruchsvollsten Projekten bei führenden Unternehmen.",
      benefit1: "Arbeiten Sie an herausfordernden Projekten bei 100+ führenden Unternehmen und Marktführern",
      benefit2: "Bauen Sie Ihre Expertise und Ihr professionelles Netzwerk unter Anleitung von Senior-Experten aus",
      benefit3: "Flexible Aufträge, die perfekt zu Ihrem Level passen - von Einsteiger bis Senior",
      benefit4: "Faire, transparente Tarife und immer pünktlich bezahlt - kein Ärger",
      tag: "New Data Frontier für Berater",
      cta: "Starten Sie Ihre Karriere als Experten-Berater",
      fullFormCta: "Oder füllen Sie direkt das vollständige Formular aus →",
      testimonial:
        "Nach einem Jahr bei Robeco über New Data Frontier hat mir Robeco angeboten, auf einen internen Vertrag zu wechseln. Angesichts meiner tollen Zeit als externer Berater bei Robeco habe ich mich für diesen Wechsel entschieden.",
      testimonialAuthor: "Teun Tanis",
      testimonialRole: "Data Berater",
      testimonialAlt: "Berater Erfolgsgeschichte",
      heroTitle: "Wachsen Sie in Ihrer Karriere als Experten-Berater",
      heroSubtitle: "Werden Sie Teil unseres Netzwerks von 250+ Profis und erhalten Sie Zugang zu den anspruchsvollsten Projekten.",
      howItWorks: "So funktioniert es",
      howItWorksSubtitle: "Drei einfache Schritte zu herausfordernden Aufträgen",
      step1Title: "Melden Sie sich an",
      step1Desc: "Füllen Sie das Anmeldeformular aus und erzählen Sie uns von Ihrer Expertise.",
      step2Title: "Wir vermitteln Sie",
      step2Desc: "Wir finden die besten Projekte, die zu Ihrem Level passen.",
      step3Title: "Starten Sie Ihren Auftrag",
      step3Desc: "Arbeiten Sie an herausfordernden Projekten und bauen Sie Ihre Karriere aus.",
      successStoriesTitle: "Erfolgsgeschichten von Beratern",
      whatYouGet: "Was Sie bekommen",
      whatYouGetSubtitle: "Als Berater bei New Data Frontier profitieren Sie von vielen Vorteilen",
      readyToStart: "Bereit zu starten?",
      readyToStartDesc: "Melden Sie sich an und entdecken Sie die besten Projekte.",
      personalGuidance: "Persönliche Betreuung während Ihres Auftrags",
      leadingCompanies: "Projekte bei 100+ führenden Unternehmen",
      fairRates: "Faire Tarife und schnelle Bezahlung",
      fillFormDesc: "Füllen Sie das Anmeldeformular aus.",
      orFillFullForm: "Oder füllen Sie direkt das vollständige Formular aus",
    },
    consultantsPage: {
      heroTitle: "Finden Sie Ihren Platz",
      heroSubtitle: "Ob Student, Berufseinsteiger oder erfahrener Profi, wir haben eine Marke, die genau zu Ihrem Niveau und Ihren Ambitionen passt.",
      heroCta: "Entdecken Sie, wohin Sie gehören",
      funnelTitle: "Ein Datenprofi für jede Frage",
      funnelSubtitle: "Suchen Sie einen bestimmten Datenprofessional? Treffen Sie unten Ihre Wahl. Für alle anderen Fragen kontaktieren Sie uns.",
      datastudentName: "DataStudent",
      datastudentTarget: "Junior Data Professionals",
      datastudentDesc: "Noch im Studium oder gerade abgeschlossen? Bei DataStudent sammeln Sie Berufserfahrung durch Arbeitnehmerüberlassung bei führenden Unternehmen.",
      datastudentCta: "Bei DataStudent anmelden",
      newdataselectName: "New Data Select",
      newdataselectTarget: "Rekrutierung & Auswahl",
      newdataselectDesc: "Bereit für den nächsten Karriereschritt? New Data Select vermittelt Sie an die besten Arbeitgeber durch Recruiting & Selection.",
      newdataselectCta: "Chancen bei New Data Select ansehen",
      dutchdatacompanyName: "Dutch Data Company",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyDesc: "Erfahrener Spezialist oder Freelancer? Dutch Data Company bietet projektbasierte Aufträge bei Organisationen, die mit Daten und KI echten Impact erzielen wollen.",
      dutchdatacompanyCta: "Projekte bei Dutch Data Company ansehen",
      howTitle: "So funktioniert es",
      howSubtitle: "In drei Schritten bei der passenden Marke starten",
      step1Title: "Wählen Sie Ihre Marke",
      step1Desc: "Finden Sie heraus, welche Marke zu Ihrer Erfahrung und Ihren Ambitionen passt.",
      step2Title: "Anmelden",
      step2Desc: "Gehen Sie zur Website der Marke und registrieren Sie sich. Einfach und schnell.",
      step3Title: "Loslegen",
      step3Desc: "Wir matchen Sie mit den besten Aufträgen und Sie starten direkt.",
      ctaTitle: "Bereit zu starten?",
      ctaSubtitle: "Wählen Sie die passende Marke und machen Sie heute den ersten Schritt in Ihrer Datenkarriere.",
    },
    expertise: {
      tag: "Unsere Expertise",
      title: "Spezialisten für Data, KI und Tech",
      subtitle:
        "Softwareentwicklung, Data Scientists, Datenanalytiker, Data Engineers, Dashboarding, SQL, Java-Entwickler, Beratung, Migration, DataScans, Hackathons, Datenbereinigung und Datenlabeling. Sowohl für IT/Data-Experten als auch für KMU-Eigentümer, die Qualität, Prozessoptimierung und Agile/Scrum-Methoden schätzen.",
      area1: "Data Engineering & SQL",
      area1desc:
        "Von Datenerfassung bis zu Pipelines und Data Warehouses. Unsere Data Engineers bauen robuste Infrastrukturen auf, optimieren SQL-Abfragen und sorgen für nahtlose Datenmigrationen.",
      area2: "KI & Machine Learning",
      area2desc:
        "Prädiktive Modelle, natürliche Sprachverarbeitung und Computer Vision. Unsere KI-Experten bringen modernste Machine Learning und Datenlabeling in Ihre Organisation.",
      area3: "Data Science & Analytics",
      area3desc:
        "Verwandeln Sie Daten in wertvolle Erkenntnisse. Von Datenbereinigung bis zu fortgeschrittenen statistischen Modellen und DataScans - unsere Data Scientists helfen Ihnen, datengestützte Entscheidungen zu treffen.",
      area4: "Softwareentwicklung",
      area4desc:
        "Full-Stack-Entwicklung mit Java, Python und modernen Frameworks. Unsere Entwickler erstellen skalierbare Anwendungen, APIs und Microservices nach Agile/Scrum-Methoden.",
      area5: "Dashboarding & BI",
      area5desc:
        "Interaktive Dashboards und Berichte, die tatsächlich genutzt werden. Unsere BI-Spezialisten sorgen dafür, dass Ihre Daten visuell und verständlich werden, mit schlanken und optimierten Prozessen.",
      area6: "Beratung & Veranstaltungen",
      area6desc:
        "Strategische Beratung zur Prozessoptimierung und digitalen Transformation. Von Hackathons bis Workshops - wir helfen Ihnen mit Wissenstransfer und praxisnahen Innovationssessions.",
    },
    clients: {
      title: "Vertraut von führenden Organisationen",
      subtitle: "Von Startup bis Corporate - unsere Experten liefern Mehrwert für Organisationen jeder Größe",
    },
    projects: {
      title: "Führende Projekte",
      subtitle:
        "Von KI im Sport bis zur maritimen Innovation - unsere Experten arbeiten an den anspruchsvollsten Projekten",
      project1: "ZAIL: KI im Wettkampfsegeln",
      project1desc: "Fortschrittliche KI-Systeme für strategische Entscheidungen bei internationalen Segelwettbewerben",
      project2: "Dashboarding für medizinische Forschung",
      project2desc: "Datenanalyse und Visualisierungs-Dashboards für wissenschaftliche Forschung an der Medizinischen Hochschule Hannover",
      project3: "Ronald McDonald Digital Support",
      project3desc: "Digitale Transformation und datengestützte Optimierung von Prozessen und Dienstleistungen",
      project4: "STERK",
      project4desc: "Eine Zusammenarbeit von Fitter Nederland, Erik Wegewijs und Bas Kremer (Special Forces VIP) bei der wir als DataStudent ihre Datenmission unterstützen",
    },
    testimonials: {
      title: "Erfolgsgeschichten",
      subtitle: "Entdecken Sie, wie Unternehmen und Berater mit New Data Frontier erfolgreich sind",
      anna: {
        quote: "Die Zusammenarbeit mit DataStudent war eine außergewöhnliche Erfahrung für unser Forschungsteam an der Medizinischen Hochschule Hannover. Zwei hochqualifizierte Studenten unterstützten uns remote und trugen zu den statistischen Analysen und Datenvisualisierungen für mehrere unserer Forschungspaper bei. Trotz der vollständig remote Arbeit waren ihre Professionalität und ihre Fähigkeit, qualitativ hochwertige Ergebnisse zu liefern, bemerkenswert.",
        role: "Medizinische Hochschule Hannover",
      },
      jesus: {
        quote: "Während meines Mathe-Masters wollte ich praktische Erfahrung sammeln, um mein Studium zu ergänzen, und DataStudent gab mir genau das, was ich brauchte. Dies bot mir die Möglichkeit, meine Programmierkenntnisse zu üben, und noch wichtiger, meine Kommunikationsfähigkeiten. Diese Arbeit führte zu einem veröffentlichten Forschungsbrief und half mir definitiv dabei, ein besserer Kandidat für meine aktuelle Promotion zu werden.",
        role: "PhD-Student Dartmouth, ehemaliger DataStudent-Berater",
      },
      gido: {
        quote: "Als Managing Partner des Highberg People Analytics & Research Teams suche ich regelmäßig nach aufstrebenden Data & Analytics Talenten. Datastudent kennt diesen Talentmarkt wie kein anderer. Darüber hinaus bietet Datastudent einen persönlichen, pragmatischen Ansatz, bei dem sie auch den Inhalt des Fachgebiets verstehen. Kurz gesagt, ich kann Datastudent definitiv als Partner empfehlen!",
        role: "Managing Partner bei Highberg",
      },
      teun: {
        quote: "Nach Abschluss meines Masters in Data Science hat mir Datastudent geholfen, meine ersten Schritte als Data Professional zu machen. Durch mehrere gute Gespräche mit Luc bin ich bei Robeco als Business Intelligence Specialist gelandet. Nach einem Jahr bei Robeco über Datastudent hat mir Robeco angeboten, auf einen internen Vertrag zu wechseln.",
        role: "Data Engineer bei Robeco, ehemaliger DataStudent-Berater",
      },
      stan: {
        quote: "Seit April 2024 arbeite ich bei Datastudent, wo ich die Möglichkeit bekommen habe, mein Wissen in der Praxis anzuwenden. Bei Flanderijn arbeite ich mit einem Team daran, umfangreiche Datensätze in klare und visuell ansprechende Dashboards umzuwandeln. Diese Erfahrung hat mir geholfen, nicht nur meine technischen Fähigkeiten zu verbessern, sondern auch meine Kommunikations- und Teamfähigkeiten zu entwickeln.",
        role: "Data Analyst bei Flanderijn via DataStudent",
      },
    },
    about: {
      title: "Das ist New Data Frontier",
      subtitle1:
        "New Data Frontier ist die Brücke zwischen ambitionierten Unternehmen und Experten-Beratern. Mit unserem Netzwerk spezialisierter Fachleute auf jedem Niveau - vom Einsteiger bis zum Senior - sind wir Marktführer in Data-, KI- und Tech-Beratung.",
      subtitle2:
        "Unsere Mission ist klar: Wir machen Data-, KI- und Tech-Expertise für jede Organisation zugänglich - von Startup bis Corporate - und geben Fachleuten die Möglichkeit, ihr Wissen bei echten Herausforderungen einzusetzen.",
      stat1: "Experten und Talente",
      stat2: "Zufriedene Unternehmen",
      stat3: "Erfolgreiche Matches",
      stat4: "Jahre Expertise",
      network: "Teil unseres Netzwerks",
      networkIntro: "New Data Frontier ist der Dachverband unter dem drei spezialisierte Marken operieren:",
      networkAnd: "und",
      networkStarters: "",
      networkBeta: "DataStudent übernimmt die Entsendung von Studenten und Einsteigern. New Data Select ist auf Personalvermittlung für Fachkräfte mit 0-7 Jahren Erfahrung spezialisiert. Dutch Data Company bietet projektbasierte Einsätze von Fortgeschrittenen, Senioren und Freelancern.",
      networkFulltime: "Zusammen bilden wir einen One-Stop-Shop für Data, KI und Tech - von jungen Talenten bis zu erfahrenen Spezialisten.",
    },
    why: {
      title: "Warum New Data Frontier wählen?",
      subtitle: "Wir zeichnen uns durch unseren bewährten Ansatz, Qualität und persönlichen Service aus",
      reason1: "Bewährte Erfolgsbilanz",
      reason1desc: "8+ Jahre Erfahrung, 95%+ erfolgreiche Matches und 100+ zufriedene Unternehmen",
      reason2: "Schnelle Vermittlung",
      reason2desc: "Oft innerhalb eines Monats gestartet, Eilplatzierung innerhalb von 2 Wochen möglich",
      reason3: "Qualität garantiert",
      reason3desc: "Alle Berater sind geprüft, zertifiziert und haben nachgewiesene Erfahrung",
      reason4: "Transparente Preise",
      reason4desc: "Klare Tarife ohne versteckte Kosten - Sie wissen genau, was auf Sie zukommt",
      reason5: "Persönlicher Ansatz",
      reason5desc: "Direkter Kontakt mit Entscheidungsträgern und Maßanfertigung bei jedem Match",
      reason6: "Breites Netzwerk",
      reason6desc: "250+ Experten und Berater auf jedem Niveau und in verschiedenen Spezialisierungen",
      ctaTitle: "Bereit für den nächsten Schritt?",
      ctaDescription: "Ob Sie nach der richtigen Expertise für Ihre Organisation suchen oder nach einem herausfordernden Projekt, das Ihre Karriere voranbringt - wir sind hier, um zu helfen.",
      ctaButton1: "Jetzt starten",
      ctaButton2: "Gespräch planen",
    },
    pricing: {
      title: "Unsere Dienste und Tarife",
      subtitle: "Drei Marken, drei Dienstleistungen - jeweils auf Ihre spezifischen Bedürfnisse abgestimmt.",
      datastudent: "DataStudent",
      datastudentService: "Entsendung",
      datastudentDesc: "Flexibler Einsatz von Studenten und Einsteigern auf Stundenbasis",
      datastudentTarget: "Junior Data Professionals",
      datastudentNote: "Ideal für Datenaufträge, bei denen junge Talente sofort Wirkung zeigen",
      rush: "Eilplatzierung (innerhalb 2 Wochen):",
      rushPrice: "€80",
      perHour: "pro Stunde",
      noMinimum: "Keine Mindeststunden, flexibler Einsatz",
      newdataselect: "New Data Select",
      newdataselectService: "Personalvermittlung",
      newdataselectDesc: "Dauerhafte Platzierung von Data- und Tech-Fachkräften",
      newdataselectTarget: "Rekrutierung & Auswahl",
      newdataselectBenefit1: "Umfassendes Assessment und Matching",
      newdataselectBenefit2: "Garantiezeit bei jeder Platzierung",
      newdataselectBenefit3: "Feste Gebühr basierend auf Jahresgehalt",
      newdataselectPrice: "Auf Angebotsbasis",
      dutchdatacompany: "Dutch Data Company",
      dutchdatacompanyService: "Projekte & Beratung",
      dutchdatacompanyDesc: "Erfahrene Spezialisten für komplexe Data- und KI-Projekte",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyBenefit1: "Projektbasierter Einsatz mit klarem Umfang",
      dutchdatacompanyBenefit2: "Zugang zu Senior-Spezialisten und Freelancern",
      dutchdatacompanyPrice: "Auf Angebotsbasis",
      forCompanies: "Für Unternehmen",
      forCompaniesDesc: "Erhalten Sie auf einen Blick Einblick in unsere Experten, Arbeitsweise und Preise.",
      downloadBrochure: "Broschüre herunterladen (PDF)",
      brochureNote: "Kein Verkaufsgespräch, nur klare Informationen in 5 Minuten.",
      planMeeting: "Gespräch planen",
      planMeetingDesc: "Besprechen Sie unverbindlich Ihre Situation mit einem unserer Experten.",
      schedule: "Termin vereinbaren",
      scheduleNote: "Wählen Sie einen Zeitpunkt, der Ihnen am besten passt.",
    },
    team: {
      title: "Persönlicher Kontakt, kurze Wege",
      subtitle:
        "Bei New Data Frontier glauben wir, dass die besten Ergebnisse durch echten Kontakt entstehen. Keine langen Ketten von Account-Managern - Sie sprechen direkt mit den Menschen, die den Unterschied machen.",
      founder: "Gründer & Senior Strategic Partner",
      founderAI: "Gründer CEO & Senior Data-Experte",
      linkedin: "LinkedIn-Profil",
      closing:
        "Rufen Sie uns an, mailen oder schreiben Sie uns - wir melden uns persönlich und denken gerne mit Ihnen mit.",
    },
    mission: {
      title: "Unsere Mission und Wirkung",
      missionTitle: "Mission",
      missionText:
        "Data-, KI- und Tech-Expertise für jede Organisation zugänglich machen, vom Startup bis zum Corporate.",
      impactTitle: "Soziale Wirkung",
      impactText:
        "Wir helfen Unternehmen und Menschen mit Abstand zum Arbeitsmarkt. Arbeiten pro bono für Wohltätigkeitsorganisationen mit sozialer Wirkung.",
      sdgTitle: "SDGs",
      sdgText: "Wir tragen aktiv zu den Zielen für nachhaltige Entwicklung der Vereinten Nationen bei.",
      viewSdg: "Unsere SDGs ansehen →",
      charitiesTitle: "Wohltätigkeitsorganisationen, die wir unterstützen",
      charitiesNote:
        "Darüber hinaus haben wir mehr als 20 Studien- und Studentenvereinigungen in den Niederlanden gesponsert.",
      applyText: "Möchten Sie sich für pro bono Arbeit oder Sponsoring bewerben? Kontaktieren Sie uns über",
    },
    contact: {
      title: "Starten Sie heute mit New Data Frontier",
      subtitle:
        "Ob Sie die richtige Expertise für Ihr Projekt suchen oder herausfordernde Aufträge, die Ihre Karriere auf die nächste Stufe bringen - wir vermitteln Sie mit dem perfekten Partner. Mit 8+ Jahren Erfahrung und 250+ Experten in unserem Netzwerk machen wir den Unterschied.",
      downloadBrochure: "Unsere Broschüre herunterladen",
      tabCompany: "Ich bin ein Unternehmen",
      tabConsultant: "Ich bin Berater",
      formTitleCompany: "Als Unternehmen registrieren",
      formTitleConsultant: "Als Berater registrieren",
      formDescCompany:
        "Füllen Sie das unten stehende Formular aus und wir helfen Ihnen, die richtige Expertise zu finden.",
      formDescConsultant:
        "Registrieren Sie sich und entdecken Sie herausfordernde Projekte, die Ihrem Niveau und Ihren Ambitionen entsprechen.",
      companyName: "Firmenname",
      fullName: "Vollständiger Name",
      email: "E-Mail-Adresse",
      phone: "Telefonnummer",
      experience: "Erfahrungsniveau",
      experiencePlaceholder: "Wählen Sie Ihr Niveau",
      starter: "Einsteiger (0-2 Jahre)",
      medior: "Fortgeschritten (2-5 Jahre)",
      senior: "Senior (5+ Jahre)",
      messageCompany: "Wie können wir Ihnen helfen?",
      messageConsultant: "Erzählen Sie uns von Ihrer Expertise",
      messagePlaceholder: "Ihre Nachricht...",
      submit: "Nachricht senden",
      successTitle: "Vielen Dank für Ihre Nachricht!",
      successDesc: "Wir werden uns innerhalb von 24 Stunden bei Ihnen melden.",
    },
    contactForm: {
      companyTitle: "Als Unternehmen registrieren",
      consultantTitle: "Als Berater registrieren",
      companyDescription: "Füllen Sie das Formular aus und wir helfen Ihnen, die richtige Expertise zu finden.",
      consultantDescription: "Melden Sie sich an und entdecken Sie herausfordernde Projekte.",
      companyName: "Firmenname",
      fullName: "Vollständiger Name",
      companyNamePlaceholder: "Ihr Firmenname",
      fullNamePlaceholder: "Ihr Name",
      email: "E-Mail-Adresse",
      emailPlaceholder: "name@unternehmen.de",
      phone: "Telefonnummer",
      experienceLevel: "Erfahrungsniveau",
      selectLevel: "Wählen Sie Ihr Niveau",
      starter: "Einsteiger (0-2 Jahre)",
      medior: "Fortgeschritten (2-5 Jahre)",
      senior: "Senior (5+ Jahre)",
      companyMessage: "Wie können wir Ihnen helfen?",
      consultantMessage: "Erzählen Sie uns von Ihrer Expertise",
      companyMessagePlaceholder: "Beschreiben Sie Ihren Bedarf an Expertise...",
      consultantMessagePlaceholder: "Beschreiben Sie Ihren Hintergrund und was Sie suchen...",
      submitting: "Wird gesendet...",
      submit: "Anmeldung absenden",
      successTitle: "Vielen Dank für Ihre Nachricht!",
      successDescription: "Wir werden uns innerhalb von 24 Stunden bei Ihnen melden.",
      errorTitle: "Etwas ist schief gelaufen",
      errorDescription: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.",
    },
    footer: {
      whoWeAre: "Wir sind",
      description:
        "Der Marktführer in Data-, KI- und Tech-Beratung. Verbinden von Unternehmen mit Experten-Beratern seit 2017.",
      forCompanies: "Für Unternehmen",
      hireExpertise: "Expertise beauftragen",
      expertiseAreas: "Expertisebereiche",
      successStories: "Erfolgsgeschichten",
      forConsultants: "Für Berater",
      findProjects: "Projekte finden",
      careerDev: "Karriereentwicklung",
      aboutUs: "Über uns",
      contact: "Kontakt",
      rights: "Alle Rechte vorbehalten.",
      privacy: "Datenschutz",
      terms: "Allgemeine Geschäftsbedingungen",
      partners: "Partnernetzwerk",
    },
    blog: {
      title: "Blog & Nachrichten",
      subtitle: "Bleiben Sie auf dem Laufenden mit den neuesten Nachrichten, Einblicken und Trends in Data, KI und Beratung.",
      seoTitle: "Blog & Nachrichten - New Data Frontier",
      seoDescription: "Lesen Sie die neuesten Nachrichten und Einblicke von New Data Frontier über Datenstrategie, KI, Cybersicherheit, Governance und Talentmanagement.",
      searchPlaceholder: "Artikel durchsuchen...",
      filterCategory: "Nach Kategorie filtern",
      allCategories: "Alle Kategorien",
      noResults: "Keine Artikel für diese Suche gefunden.",
      noPosts: "Es wurden noch keine Blogartikel veröffentlicht.",
      notFoundTitle: "Artikel nicht gefunden",
      notFoundDesc: "Der gesuchte Artikel existiert nicht oder ist nicht mehr verfügbar.",
      backToBlog: "Zurück zum Blog",
      readArticle: "Lesen Sie",
      authorRole: "Inhaber New Data Frontier",
    },
    vacatures: {
      title: "Stellenangebote",
      subtitle: "Entdecken Sie aktuelle Stellenangebote in Data, KI und Tech über unser Netzwerk bei deBetaStudent.",
      seoTitle: "Stellenangebote Data & KI",
      seoDescription: "Aktuelle Stellenangebote in Data, KI und Tech ansehen. Automatisch synchronisiert mit deBetaStudent.nl.",
      breadcrumb: "Stellenangebote",
      searchPlaceholder: "Stellen durchsuchen...",
      filterCategory: "Kategorie",
      filterLocation: "Standort",
      allCategories: "Alle Kategorien",
      allLocations: "Alle Standorte",
      resultsFound: "Stellen gefunden",
      viewJob: "Stelle ansehen",
      noResults: "Keine Stellen für diese Suche gefunden.",
      noVacatures: "Derzeit sind keine Stellenangebote verfügbar.",
      checkBack: "Alle Stellen ansehen auf",
      poweredBy: "Stellen über",
      ctaTitle: "Auf der Suche nach mehr Möglichkeiten?",
      ctaDescription: "Alle Stellen auf deBetaStudent.nl ansehen oder als Berater bei New Data Frontier anmelden.",
      ctaButton1: "Alle Stellen auf deBetaStudent.nl",
      ctaButton2: "Berater werden",
    },
    organisation: {
      seoTitle: "Unsere Organisation - New Data Frontier",
      seoDescription: "Drei Marken, drei Dienstleistungen, drei Ebenen. Entdecken Sie, wie New Data Frontier Datentalente auf jeder Ebene liefert.",
      tagline: "Unsere Organisation",
      heroSubtitle: "A Dutch Data Company",
      heroDescription: "Drei Marken. Drei Dienstleistungen. Drei Ebenen. Eine Mission: die richtige Datenexpertise zur richtigen Zeit.",
      brandsTitle: "Drei Marken, ein Netzwerk",
      brandsSubtitle: "Jede Marke bedient ein bestimmtes Marktsegment - damit wir immer den richtigen Match liefern können.",
      threeBrands: "Marken",
      threeServices: "Dienstleistungen",
      threeLevels: "Ebenen",
      brand1Audience: "Studenten & Einsteiger",
      brand1Service: "Arbeitnehmerüberlassung",
      brand1Desc: "Ambitionierte Studenten und Absolventen, die ihre ersten Schritte in Data, KI und Tech machen. Flexibel einsetzbar.",
      brand2Audience: "0-7 Jahre Erfahrung",
      brand2Service: "Personalvermittlung",
      brand2Desc: "Fachkräfte mit 0-7 Jahren relevanter Erfahrung, bereit für den nächsten Schritt. Vermittelt für eine dauerhafte Besetzung.",
      brand3Audience: "Fortgeschrittene & Senioren",
      brand3Service: "Projektbasiert",
      brand3Desc: "Erfahrene Spezialisten und strategische Berater für komplexe Daten- und KI-Projekte.",
      whyTitle: "Warum diese Struktur?",
      whyDescription: "In der Praxis machen wir alles - von Überlassung bis Beratung, vom Einsteiger bis zum Senior. Aber diese Struktur macht sofort deutlich, welches Niveau und welche Dienstleistung am besten passt.",
      ctaCompanies: "Ich suche Talent",
      ctaConsultants: "Ich bin Berater",
      manifestTitle: "Unser Manifest",
      manifest: "Data is the new frontier.\n\nIt promises unprecedented opportunity\nfor those who choose to pursue it.\n\nBut opportunity alone does not create growth.\n\nGrowth demands more than skills.\nMore than technology.\nMore than access.\n\nIt requires an entrepreneurial mindset.\nThe discipline to think long term.\nThe drive to act in the here and now.\n\nWe develop and deliver data professionals\nwith the mindset and drive to build sustainable growth.\n\nGrowth is the foundation of our culture.\n\nWe enable organisations to grow in this new frontier.\nAnd we develop the people who grow with it.\n\nFrom student to senior.\nFrom potential to performance.\n\nGrowth is not found.\nIt is built.",
      mountainTitle: "The Mountain",
      mountain: "It exists long before anyone climbs it.\nPowerful. Visible. Full of potential.\n\nData is no different.\n\nIt is there.\nWaiting to be explored.\nWaiting to be used.\n\nBut a mountain without infrastructure is not an opportunity.\nIt is uncharted terrain - like data without structure.\n\nGrowth begins when terrain becomes workable.\n\nPaths are defined.\nInfrastructure is built.\nCapabilities are developed.\n\nThat is where the frontier shifts -\nfrom raw landscape\nto usable ground.\n\nWhether you aim to climb higher\nor build your business on solid ground,\nthe conditions matter.\n\nWe pave the way.\n\nWe shape the landscape so sustainable growth becomes possible.\n\nGrowth is not found.\nIt is built.",
    },
  },
  fr: {
    nav: {
      companies: "Entreprises",
      consultants: "Consultants",
      expertise: "Expertise",
      success: "Réussites",
      about: "À propos",
      vacatures: "Offres d'emploi",
      contact: "Contact",
      organisation: "Notre organisation",
    },
    hero: {
      title: "Construisez la croissance avec data, IA & tech",
      subtitle: "Des professionnels de la data avec l'esprit entrepreneurial et la motivation pour faire réussir vos projets. Du débutant au senior, la croissance ne se trouve pas, elle se construit.",
      cta1: "Planifiez une consultation gratuite",
      cta2: "Notre histoire",
    },
    companies: {
      title: "Pour les Entreprises : Trouvez le Match Parfait",
      subtitle:
        "Que vous ayez un besoin urgent ou que vous recherchiez un partenaire à long terme - nous vous mettons en relation avec la bonne expertise en 48 heures.",
      benefit1: "Expertise à Tous les Niveaux",
      benefit1desc: "De débutant à consultant senior - nous avons le talent que vous recherchez",
      benefit2: "Mise en Relation Rapide",
      benefit2desc: "Premiers candidats appropriés identifiés en 48 heures",
      benefit3: "Déploiement Flexible",
      benefit3desc: "Par heure, jour, semaine ou mois - entièrement selon vos besoins",
      testimonial:
        "Grâce à New Data Frontier, nous avons trouvé le bon data engineer en 48 heures pour notre projet de migration critique.",
      testimonialAuthor: "Martijn de Vries",
      testimonialRole: "CTO chez TechScale Solutions",
      forCompanies: {
        tag: "New Data Frontier pour entreprises",
        title: "L'expertise quand et où vous en avez besoin",
        subtitle: "De débutant à senior - nous mettons votre organisation en relation avec les bons consultants.",
        benefit1: "Accès direct à 250+ consultants experts",
        benefit2: "Tarif horaire clair, pas de frais cachés",
        benefit3: "Déploiement flexible dès un jour",
        benefit4: "Historique prouvé chez 100+ leaders du marché",
        cta: "Trouvez le match parfait pour votre projet",
        testimonial: "Luc et Eli nous ont énormément aidés. La qualité de New Data Frontier a dépassé nos attentes.",
        testimonialAuthor: "Suzanne Kieft",
        testimonialRole: "Région de Transport Amsterdam",
        testimonialAlt: "Histoire de réussite New Data Frontier",
        howItWorks: "Comment ça marche",
        howItWorksSubtitle: "Trois étapes simples vers la bonne expertise",
        step1Title: "Dites-nous ce que vous cherchez",
        step1Desc: "Remplissez le formulaire de contact ou planifiez un appel.",
        step2Title: "Nous trouvons le match parfait",
        step2Desc: "Dans les 48 heures, nous sélectionnons les meilleurs candidats.",
        step3Title: "Démarrage dans un mois",
        step3Desc: "Après la présentation, nous démarrons souvent dans un mois.",
        whatClientsSay: "Ce que disent les clients",
        transparentRates: "Tarifs transparents",
        transparentRatesSubtitle: "Prix clairs sans frais cachés",
        downloadFullBrochure: "Télécharger la brochure complète",
        readyToStart: "Prêt à commencer ?",
        readyToStartDesc: "Remplissez le formulaire de contact ou contactez-nous directement.",
        planDirectCall: "Planifier un appel directement",
        sectorsTitle: "Nos secteurs",
        sectorsSubtitle: "Nous sommes agnostiques en termes de secteur - nos consultants travaillent dans tous les domaines. Grâce à des années d'expérience, nous avons développé une expertise spécifique dans les secteurs suivants :",
        sectorBouw: "Construction",
        sectorBouwDesc: "Gestion de projet basée sur les données, analyse BIM et optimisation des processus.",
        sectorZorg: "Santé",
        sectorZorgDesc: "De l'analyse des données de santé aux applications IA dans les hôpitaux.",
        sectorHR: "RH",
        sectorHRDesc: "People analytics, planification des effectifs et transformation RH basée sur les données.",
        sectorPE: "Private Equity",
        sectorPEDesc: "Analyse de données de due diligence, suivi de portefeuille et création de valeur.",
        sectorAutomotive: "Automobile",
        sectorAutomotiveDesc: "Données de production, analyses de la chaîne d'approvisionnement et optimisation qualité.",
        sectorScheepvaart: "Maritime",
        sectorScheepvaartDesc: "Analyse de données maritimes, optimisation d'itinéraires et logistique portuaire.",
      },
    },
    consultants: {
      title: "Pour les Consultants : Développez Votre Carrière",
      subtitle: "Rejoignez notre réseau et accédez aux projets les plus stimulants dans les entreprises leaders.",
      benefit1: "Travaillez sur des projets stimulants dans 100+ entreprises leaders et chefs de marché",
      benefit2: "Développez votre expertise et votre réseau professionnel sous la direction d'experts seniors",
      benefit3: "Missions flexibles parfaitement adaptées à votre niveau - de débutant à senior",
      benefit4: "Tarifs équitables et transparents, toujours payés à temps - sans tracas",
      tag: "New Data Frontier pour consultants",
      cta: "Lancez votre carrière de consultant expert",
      fullFormCta: "Ou remplissez directement le formulaire complet →",
      testimonial:
        "Après un an chez Robeco via New Data Frontier, Robeco m'a proposé de passer à un contrat interne. Compte tenu de mon excellente expérience en tant que consultant externe chez Robeco, j'ai décidé de faire cette transition.",
      testimonialAuthor: "Teun Tanis",
      testimonialRole: "Consultant Data",
      testimonialAlt: "Histoire de réussite consultant",
      heroTitle: "Développez votre carrière de consultant expert",
      heroSubtitle: "Rejoignez notre réseau de 250+ professionnels et accédez aux projets les plus stimulants.",
      howItWorks: "Comment ça marche",
      howItWorksSubtitle: "Trois étapes simples vers des missions stimulantes",
      step1Title: "Inscrivez-vous",
      step1Desc: "Remplissez le formulaire d'inscription et parlez-nous de votre expertise.",
      step2Title: "Nous vous mettons en relation",
      step2Desc: "Nous trouvons les meilleurs projets qui correspondent à votre niveau.",
      step3Title: "Commencez votre mission",
      step3Desc: "Travaillez sur des projets stimulants et développez votre carrière.",
      successStoriesTitle: "Histoires de réussite de consultants",
      whatYouGet: "Ce que vous obtenez",
      whatYouGetSubtitle: "En tant que consultant chez New Data Frontier, vous bénéficiez de nombreux avantages",
      readyToStart: "Prêt à commencer ?",
      readyToStartDesc: "Inscrivez-vous et découvrez les meilleurs projets.",
      personalGuidance: "Accompagnement personnel pendant votre mission",
      leadingCompanies: "Projets dans 100+ entreprises leaders",
      fairRates: "Tarifs équitables et paiement rapide",
      fillFormDesc: "Remplissez le formulaire d'inscription.",
      orFillFullForm: "Ou remplissez directement le formulaire complet",
    },
    consultantsPage: {
      heroTitle: "Trouvez la place qui vous convient",
      heroSubtitle: "Que vous soyez étudiant, débutant ou professionnel expérimenté, nous avons une marque qui correspond parfaitement à votre niveau et vos ambitions.",
      heroCta: "Découvrez où vous appartenez",
      funnelTitle: "Un professionnel des données pour chaque besoin",
      funnelSubtitle: "Vous cherchez un professionnel des données spécifique ? Faites votre choix ci-dessous. Pour toutes autres questions, contactez-nous.",
      datastudentName: "DataStudent",
      datastudentTarget: "Junior Data Professionals",
      datastudentDesc: "Encore étudiant ou récemment diplômé ? Chez DataStudent, vous acquérez de l'expérience professionnelle via le détachement dans des entreprises leaders.",
      datastudentCta: "S'inscrire chez DataStudent",
      newdataselectName: "New Data Select",
      newdataselectTarget: "Recrutement & Sélection",
      newdataselectDesc: "Prêt pour la prochaine étape de votre carrière ? New Data Select vous met en relation avec les meilleurs employeurs via le recrutement & sélection.",
      newdataselectCta: "Voir les opportunités chez New Data Select",
      dutchdatacompanyName: "Dutch Data Company",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyDesc: "Spécialiste expérimenté ou freelance ? Dutch Data Company propose des missions projets dans des organisations qui veulent créer un vrai impact avec les données et l'IA.",
      dutchdatacompanyCta: "Voir les projets chez Dutch Data Company",
      howTitle: "Comment ça marche",
      howSubtitle: "Trois étapes pour commencer chez la marque qui vous correspond",
      step1Title: "Choisissez votre marque",
      step1Desc: "Découvrez quelle marque correspond à votre expérience et vos ambitions.",
      step2Title: "Inscrivez-vous",
      step2Desc: "Rendez-vous sur le site de la marque et inscrivez-vous. Simple et rapide.",
      step3Title: "Commencez à travailler",
      step3Desc: "Nous vous mettons en relation avec les meilleures missions et vous démarrez immédiatement.",
      ctaTitle: "Prêt à commencer ?",
      ctaSubtitle: "Choisissez la marque qui vous convient et faites le premier pas dans votre carrière data aujourd'hui.",
    },
    expertise: {
      tag: "Notre Expertise",
      title: "Spécialistes en Data, IA et Tech",
      subtitle:
        "Développement logiciel, data scientists, analystes de données, data engineers, tableaux de bord, SQL, développeurs Java, conseil, migration, dataScans, hackathons, nettoyage de données et labellisation de données. Pour l'expert IT/Data comme pour le propriétaire de PME qui valorise la qualité, l'optimisation des processus et les méthodologies Agile/Scrum.",
      area1: "Data Engineering & SQL",
      area1desc:
        "De la collecte de données aux pipelines et entrepôts de données. Nos data engineers construisent des infrastructures robustes, optimisent les requêtes SQL et assurent des migrations de données fluides.",
      area2: "IA & Machine Learning",
      area2desc:
        "Modèles prédictifs, traitement du langage naturel et vision par ordinateur. Nos experts IA apportent le machine learning de pointe et la labellisation de données à votre organisation.",
      area3: "Data Science & Analytics",
      area3desc:
        "Transformez les données en insights précieux. Du nettoyage de données aux modèles statistiques avancés et dataScans - nos data scientists vous aident à prendre des décisions basées sur les données.",
      area4: "Développement Logiciel",
      area4desc:
        "Développement full-stack avec Java, Python et frameworks modernes. Nos développeurs construisent des applications évolutives, des APIs et des microservices selon les méthodologies Agile/Scrum.",
      area5: "Tableaux de Bord & BI",
      area5desc:
        "Tableaux de bord interactifs et rapports réellement utilisés. Nos spécialistes BI rendent vos données visuelles et compréhensibles, avec des processus lean et optimisés.",
      area6: "Conseil & Événements",
      area6desc:
        "Conseil stratégique pour l'optimisation des processus et la transformation digitale. Des hackathons aux ateliers - nous vous aidons avec le transfert de connaissances et des sessions d'innovation pratiques.",
    },
    clients: {
      title: "Approuvé par des Organisations Leaders",
      subtitle: "De la startup au corporate - nos experts créent de la valeur pour des organisations de toutes tailles",
    },
    projects: {
      title: "Projets Leaders",
      subtitle:
        "De l'IA dans le sport à l'innovation maritime - nos experts travaillent sur les projets les plus stimulants",
      project1: "ZAIL : IA dans la Voile Compétitive",
      project1desc:
        "Systèmes IA avancés pour la prise de décision stratégique lors de compétitions de voile internationales",
      project2: "Tableaux de bord pour la recherche médicale",
      project2desc: "Analyse de données et tableaux de bord de visualisation pour la recherche scientifique à la Medizinische Hochschule Hannover",
      project3: "Support Digital Ronald McDonald",
      project3desc: "Transformation digitale et optimisation basée sur les données des processus et services",
      project4: "STERK",
      project4desc: "Une collaboration de Fitter Nederland, Erik Wegewijs et Bas Kremer (Special Forces VIP) où nous soutenons en tant que DataStudent leur mission data",
    },
    testimonials: {
      title: "Réussites",
      subtitle: "Découvrez comment entreprises et consultants réussissent avec New Data Frontier",
      anna: {
        quote: "La collaboration avec DataStudent a été une expérience exceptionnelle pour notre équipe de recherche à la Medizinische Hochschule Hannover. Deux étudiants hautement qualifiés nous ont soutenus à distance, contribuant aux analyses statistiques et aux visualisations de données pour plusieurs de nos articles de recherche. Malgré le travail entièrement à distance, leur professionnalisme et leur capacité à fournir des résultats de haute qualité étaient remarquables.",
        role: "Medizinische Hochschule Hannover",
      },
      jesus: {
        quote: "Pendant mon master en mathématiques, je voulais acquérir une expérience concrète pour compléter mes cours, et DataStudent m'a donné exactement ce dont j'avais besoin. Cela m'a offert l'opportunité de pratiquer mes compétences en programmation, et plus important encore, mes compétences en communication. Ce travail a abouti à une lettre de recherche publiée et m'a définitivement aidé à devenir un meilleur candidat pour mon doctorat actuel.",
        role: "Doctorant Dartmouth, ancien consultant DataStudent",
      },
      gido: {
        quote: "En tant que Managing Partner de l'équipe Highberg People Analytics & Research, je recherche régulièrement des talents débutants en Data & Analytics. Datastudent connaît ce marché des talents comme personne. De plus, Datastudent offre une approche personnelle et pragmatique où ils comprennent également le contenu du domaine. En bref, je recommande vraiment Datastudent comme partenaire!",
        role: "Managing Partner chez Highberg",
      },
      teun: {
        quote: "Après avoir terminé mon master en Data Science, Datastudent m'a aidé à faire mes premiers pas en tant que professionnel des données. Grâce à plusieurs bonnes conversations avec Luc, je me suis retrouvé chez Robeco en tant que Spécialiste Business Intelligence. Après un an chez Robeco via Datastudent, Robeco m'a proposé de passer à un contrat interne.",
        role: "Data Engineer chez Robeco, ancien consultant DataStudent",
      },
      stan: {
        quote: "Depuis avril 2024, je travaille chez Datastudent, où j'ai eu l'opportunité d'appliquer mes connaissances dans la pratique. Chez Flanderijn, je travaille avec une équipe pour convertir des ensembles de données étendus en tableaux de bord clairs et visuellement attrayants. Cette expérience m'a aidé à améliorer non seulement mes compétences techniques mais aussi mes compétences en communication et collaboration.",
        role: "Data Analyst chez Flanderijn via DataStudent",
      },
    },
    about: {
      title: "Voici New Data Frontier",
      subtitle1:
        "New Data Frontier est le pont entre entreprises ambitieuses et consultants experts. Avec notre réseau de professionnels spécialisés à tous les niveaux - de débutant à senior - nous sommes leaders du marché en conseil data, IA et tech.",
      subtitle2:
        "Notre mission est claire : rendre l'expertise en data, IA et tech accessible à chaque organisation - de la startup au corporate - et donner aux professionnels l'opportunité d'appliquer leurs connaissances à de vrais défis. Ainsi, les entreprises tirent plus de valeur de leurs données et technologies, tandis que les talents acquièrent une expérience précieuse et élargissent leur expertise.",
      stat1: "Experts et Talents",
      stat2: "Entreprises Satisfaites",
      stat3: "Matchs Réussis",
      stat4: "Années d'Expertise",
      network: "Partie de Notre Réseau",
      networkIntro: "New Data Frontier recrute ses talents directement des universités avec ses marques",
      networkAnd: "et",
      networkStarters: "",
      networkBeta: "Vous souhaitez mettre en avant votre poste de débutant ? Consultez deBetaStudent. Vous voulez travailler avec de jeunes talents qui apportent déjà une grande valeur ajoutée pendant leurs études ? Découvrez DataStudent.",
      networkFulltime: "Vous recherchez un soutien à temps plein, du conseil ou des professionnels expérimentés ? Vous êtes au bon endroit. De l'étudiant au professionnel - nous sommes votre guichet unique pour les données, l'IA et la tech.",
    },
    why: {
      title: "Pourquoi Choisir New Data Frontier ?",
      subtitle: "Nous nous distinguons par notre approche éprouvée, notre qualité et notre service personnel",
      reason1: "Historique Prouvé",
      reason1desc: "8+ années d'expérience, 95%+ de matchs réussis et 100+ entreprises satisfaites",
      reason2: "Mise en Relation Rapide",
      reason2desc: "Souvent commencé dans un mois, placement urgent dans 2 semaines possible",
      reason3: "Qualité Garantie",
      reason3desc: "Tous les consultants sont vérifiés, certifiés et ont une expérience prouvée",
      reason4: "Prix Transparents",
      reason4desc: "Tarifs directs sans frais cachés - vous savez exactement à quoi vous attendre",
      reason5: "Approche Personnelle",
      reason5desc: "Contact direct avec les décideurs et personnalisation dans chaque match",
      reason6: "Large Réseau",
      reason6desc: "250+ experts et consultants à tous les niveaux et dans diverses spécialisations",
      ctaTitle: "Prêt à passer à l'étape suivante ?",
      ctaDescription: "Que vous cherchiez la bonne expertise pour votre organisation ou un projet stimulant qui booste votre carrière - nous sommes là pour vous aider.",
      ctaButton1: "Commencer maintenant",
      ctaButton2: "Planifier un appel",
    },
    pricing: {
      title: "Nos Services et Tarifs",
      subtitle: "Trois marques, trois services - chacun adapté à vos besoins spécifiques.",
      datastudent: "DataStudent",
      datastudentService: "Détachement",
      datastudentDesc: "Déploiement flexible d'étudiants et débutants à l'heure",
      datastudentTarget: "Junior Data Professionals",
      datastudentNote: "Idéal pour les missions data où les jeunes talents font un impact immédiat",
      rush: "Placement urgent (sous 2 semaines) :",
      rushPrice: "€80",
      perHour: "par heure",
      noMinimum: "Pas d'heures minimum, déploiement flexible",
      newdataselect: "New Data Select",
      newdataselectService: "Recrutement",
      newdataselectDesc: "Placement permanent de professionnels data et tech",
      newdataselectTarget: "Recrutement & Sélection",
      newdataselectBenefit1: "Évaluation et matching complets",
      newdataselectBenefit2: "Période de garantie sur chaque placement",
      newdataselectBenefit3: "Frais fixes basés sur le salaire annuel",
      newdataselectPrice: "Sur devis",
      dutchdatacompany: "Dutch Data Company",
      dutchdatacompanyService: "Projets & Conseil",
      dutchdatacompanyDesc: "Spécialistes expérimentés pour projets data et IA complexes",
      dutchdatacompanyTarget: "Senior Data Experts",
      dutchdatacompanyBenefit1: "Déploiement projet avec périmètre clair",
      dutchdatacompanyBenefit2: "Accès à des spécialistes seniors et freelances",
      dutchdatacompanyPrice: "Sur devis",
      forCompanies: "Pour les Entreprises",
      forCompaniesDesc: "Aperçu rapide de nos experts, notre approche et nos prix.",
      downloadBrochure: "Télécharger la Brochure (PDF)",
      brochureNote: "Pas de discours commercial, juste des informations claires en 5 minutes.",
      planMeeting: "Planifier un Entretien",
      planMeetingDesc: "Discutez de votre situation avec l'un de nos experts sans engagement.",
      schedule: "Prendre un Rendez-vous",
      scheduleNote: "Choisissez un moment qui vous convient le mieux.",
    },
    team: {
      title: "Contact personnel, lignes courtes",
      subtitle:
        "Chez New Data Frontier, nous croyons que les meilleurs résultats naissent d'un vrai contact. Pas de longues chaînes de gestionnaires de comptes - vous échangez directement avec les personnes qui font la différence.",
      founder: "Fondateur & Senior Strategic Partner",
      founderAI: "Fondateur CEO & Expert Senior Data",
      linkedin: "Profil LinkedIn",
      closing:
        "Appelez, envoyez un e-mail ou un message - nous répondons personnellement et réfléchissons volontiers avec vous.",
    },
    mission: {
      title: "Notre Mission et Impact",
      missionTitle: "Mission",
      missionText:
        "Rendre l'expertise en data, IA et tech accessible à chaque organisation, de la startup au corporate.",
      impactTitle: "Impact Social",
      impactText:
        "Nous aidons les entreprises et les personnes éloignées du marché du travail. Travaillons pro bono pour des œuvres caritatives à impact social.",
      sdgTitle: "ODD",
      sdgText: "Nous contribuons activement aux Objectifs de Développement Durable des Nations Unies.",
      viewSdg: "Voir nos ODD →",
      charitiesTitle: "Œuvres Caritatives que Nous Soutenons",
      charitiesNote: "De plus, nous avons parrainé plus de 20 associations étudiantes aux Pays-Bas.",
      applyText: "Vous souhaitez postuler pour un travail pro bono ou un parrainage ? Contactez-nous via",
    },
    contact: {
      title: "Commencez Aujourd'hui avec New Data Frontier",
      subtitle:
        "Que vous recherchiez la bonne expertise pour votre projet ou des missions stimulantes qui font progresser votre carrière - nous vous mettons en relation avec le partenaire parfait. Avec 8+ années d'expérience et 250+ experts dans notre réseau, nous faisons la différence.",
      downloadBrochure: "Télécharger Notre Brochure",
      tabCompany: "Je suis une Entreprise",
      tabConsultant: "Je suis Consultant",
      formTitleCompany: "S'inscrire en tant qu'Entreprise",
      formTitleConsultant: "S'inscrire en tant que Consultant",
      formDescCompany: "Remplissez le formulaire ci-dessous et nous vous aiderons à trouver la bonne expertise.",
      formDescConsultant:
        "Inscrivez-vous et découvrez des projets stimulants qui correspondent à votre niveau et vos ambitions.",
      companyName: "Nom de l'Entreprise",
      fullName: "Nom Complet",
      email: "Adresse Email",
      phone: "Numéro de Téléphone",
      experience: "Niveau d'Expérience",
      experiencePlaceholder: "Sélectionnez votre niveau",
      starter: "Débutant (0-2 ans)",
      medior: "Intermédiaire (2-5 ans)",
      senior: "Senior (5+ ans)",
      messageCompany: "Comment pouvons-nous vous aider ?",
      messageConsultant: "Parlez-nous de votre expertise",
      messagePlaceholder: "Votre message...",
      submit: "Envoyer le Message",
      successTitle: "Merci pour votre message !",
      successDesc: "Nous vous contacterons dans les 24 heures.",
    },
    contactForm: {
      companyTitle: "S'inscrire en tant qu'entreprise",
      consultantTitle: "S'inscrire en tant que consultant",
      companyDescription: "Remplissez le formulaire et nous vous aiderons à trouver la bonne expertise.",
      consultantDescription: "Inscrivez-vous et découvrez des projets stimulants.",
      companyName: "Nom de l'entreprise",
      fullName: "Nom complet",
      companyNamePlaceholder: "Nom de votre entreprise",
      fullNamePlaceholder: "Votre nom",
      email: "Adresse email",
      emailPlaceholder: "nom@entreprise.fr",
      phone: "Numéro de téléphone",
      experienceLevel: "Niveau d'expérience",
      selectLevel: "Sélectionnez votre niveau",
      starter: "Débutant (0-2 ans)",
      medior: "Intermédiaire (2-5 ans)",
      senior: "Senior (5+ ans)",
      companyMessage: "Comment pouvons-nous vous aider ?",
      consultantMessage: "Parlez-nous de votre expertise",
      companyMessagePlaceholder: "Décrivez vos besoins en expertise...",
      consultantMessagePlaceholder: "Décrivez votre parcours et ce que vous recherchez...",
      submitting: "Envoi en cours...",
      submit: "Envoyer l'inscription",
      successTitle: "Merci pour votre message !",
      successDescription: "Nous vous contacterons dans les 24 heures.",
      errorTitle: "Une erreur s'est produite",
      errorDescription: "Veuillez réessayer plus tard ou nous contacter directement.",
    },
    footer: {
      whoWeAre: "Nous sommes",
      description:
        "Le leader du marché en conseil data, IA et tech. Connecter entreprises et consultants experts depuis 2017.",
      forCompanies: "Pour les Entreprises",
      hireExpertise: "Recruter l'Expertise",
      expertiseAreas: "Domaines d'Expertise",
      successStories: "Réussites",
      forConsultants: "Pour les Consultants",
      findProjects: "Trouver des Projets",
      careerDev: "Développement de Carrière",
      aboutUs: "À propos",
      contact: "Contact",
      rights: "Tous droits réservés.",
      privacy: "Confidentialité",
      terms: "Conditions Générales",
      partners: "Réseau de Partenaires",
    },
    blog: {
      title: "Blog & Actualités",
      subtitle: "Restez informé des dernières nouvelles, perspectives et tendances en data, IA et conseil.",
      seoTitle: "Blog & Actualités - New Data Frontier",
      seoDescription: "Lisez les dernières nouvelles et perspectives de New Data Frontier sur la stratégie data, l'IA, la cybersécurité, la gouvernance et la gestion des talents.",
      searchPlaceholder: "Rechercher des articles...",
      filterCategory: "Filtrer par catégorie",
      allCategories: "Toutes les catégories",
      noResults: "Aucun article trouvé pour cette recherche.",
      noPosts: "Aucun article de blog n'a encore été publié.",
      notFoundTitle: "Article introuvable",
      notFoundDesc: "L'article que vous recherchez n'existe pas ou n'est plus disponible.",
      backToBlog: "Retour au blog",
      readArticle: "Lire",
      authorRole: "Propriétaire New Data Frontier",
    },
    vacatures: {
      title: "Offres d'emploi",
      subtitle: "Découvrez les offres d'emploi actuelles en data, IA et tech via notre réseau chez deBetaStudent.",
      seoTitle: "Offres d'emploi Data & IA",
      seoDescription: "Consultez les offres d'emploi actuelles en data, IA et tech. Synchronisé automatiquement avec deBetaStudent.nl.",
      breadcrumb: "Offres d'emploi",
      searchPlaceholder: "Rechercher des offres...",
      filterCategory: "Catégorie",
      filterLocation: "Localisation",
      allCategories: "Toutes les catégories",
      allLocations: "Toutes les localisations",
      resultsFound: "offres trouvées",
      viewJob: "Voir l'offre",
      noResults: "Aucune offre trouvée pour cette recherche.",
      noVacatures: "Aucune offre d'emploi disponible pour le moment.",
      checkBack: "Voir toutes les offres sur",
      poweredBy: "Offres via",
      ctaTitle: "À la recherche de plus d'opportunités ?",
      ctaDescription: "Consultez toutes les offres sur deBetaStudent.nl ou inscrivez-vous comme consultant chez New Data Frontier.",
      ctaButton1: "Toutes les offres sur deBetaStudent.nl",
      ctaButton2: "Devenir consultant",
    },
    organisation: {
      seoTitle: "Notre Organisation - New Data Frontier",
      seoDescription: "Trois marques, trois services, trois niveaux. Découvrez comment New Data Frontier fournit des talents data à chaque niveau.",
      tagline: "Notre organisation",
      heroSubtitle: "A Dutch Data Company",
      heroDescription: "Trois marques. Trois services. Trois niveaux. Une mission : la bonne expertise data au bon moment.",
      brandsTitle: "Trois marques, un réseau",
      brandsSubtitle: "Chaque marque sert un segment de marché spécifique - pour toujours trouver le bon match.",
      threeBrands: "Marques",
      threeServices: "Services",
      threeLevels: "Niveaux",
      brand1Audience: "Étudiants & débutants",
      brand1Service: "Détachement",
      brand1Desc: "Étudiants ambitieux et jeunes diplômés faisant leurs premiers pas dans les données, l'IA et la tech. Déployés de manière flexible.",
      brand2Audience: "Débutants & intermédiaires",
      brand2Service: "Recrutement",
      brand2Desc: "Professionnels avec 1-5 ans d'expérience prêts pour l'étape suivante. Placés via recrutement pour un match durable.",
      brand3Audience: "Intermédiaires & seniors",
      brand3Service: "Par projet",
      brand3Desc: "Spécialistes expérimentés et conseillers stratégiques déployés sur une base de projet pour des défis complexes en données et IA.",
      whyTitle: "Pourquoi cette structure ?",
      whyDescription: "En pratique, nous faisons tout - du détachement au conseil, du débutant au senior. Mais cette structure rend immédiatement clair quel niveau et quel service convient le mieux.",
      ctaCompanies: "Je cherche des talents",
      ctaConsultants: "Je suis consultant",
      manifestTitle: "Notre Manifeste",
      manifest: "Data is the new frontier.\n\nIt promises unprecedented opportunity\nfor those who choose to pursue it.\n\nBut opportunity alone does not create growth.\n\nGrowth demands more than skills.\nMore than technology.\nMore than access.\n\nIt requires an entrepreneurial mindset.\nThe discipline to think long term.\nThe drive to act in the here and now.\n\nWe develop and deliver data professionals\nwith the mindset and drive to build sustainable growth.\n\nGrowth is the foundation of our culture.\n\nWe enable organisations to grow in this new frontier.\nAnd we develop the people who grow with it.\n\nFrom student to senior.\nFrom potential to performance.\n\nGrowth is not found.\nIt is built.",
      mountainTitle: "The Mountain",
      mountain: "It exists long before anyone climbs it.\nPowerful. Visible. Full of potential.\n\nData is no different.\n\nIt is there.\nWaiting to be explored.\nWaiting to be used.\n\nBut a mountain without infrastructure is not an opportunity.\nIt is uncharted terrain - like data without structure.\n\nGrowth begins when terrain becomes workable.\n\nPaths are defined.\nInfrastructure is built.\nCapabilities are developed.\n\nThat is where the frontier shifts -\nfrom raw landscape\nto usable ground.\n\nWhether you aim to climb higher\nor build your business on solid ground,\nthe conditions matter.\n\nWe pave the way.\n\nWe shape the landscape so sustainable growth becomes possible.\n\nGrowth is not found.\nIt is built.",
    },
  },
};
